import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    ChevronLeft, User, MapPin, FileText, ClipboardCheck,
    LayoutDashboard, CheckSquare, Smartphone, Image as ImageIcon, DollarSign,
    PlusCircle, PlayCircle, Loader2, AlertCircle, FolderOpen, Calendar,
    Clock, Car, Euro, TrendingUp, Edit2, ChevronDown, History, Receipt, Download, CalendarDays,
    Tag, Check, X, ShieldCheck, UserX, AlertTriangle,
} from 'lucide-react';
import { Modal } from '../components/Modal';
import ClientBadge from '../components/ClientBadge';
import AgendaSettimanaleModal from '../components/AgendaSettimanaleModal';
import { ConflittiDisponibilitaModal, ConflittoDisponibilita } from '../components/ConflittiDisponibilitaModal';
import { PraticaService, UserService, IncaricoService, SettingsService, RelazioneGiornalieraService, ClientService, PreventiviService } from '../services/apiService';
import { Pratica, User as UserType, Files, Incarico, UserRole } from '../types';
import { useToast } from '../components/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { messaggioPermessoNegato } from '../utils/sediGestite';

// Schede
import { TabContabilita } from "../components/TabContabilita";
import { TabMedia } from "../components/TabMedia";
import { TabIncarichi } from "../components/TabIncarichi";
import LavoroAPosterioriModal from '../components/LavoroAPosterioriModal';
import { TabReport } from "../components/TabReport";
import { TabRiepilogo } from "../components/TabRiepilogo";
import NumberInput from '../components/NumberInput';
import { IvaMode, IVA_MODE_LABEL, splitIncasso, DEFAULT_INCASSO_MODE } from '../utils/contabilitaCanali';
import { COUNTRIES, findCountryCodeByName } from '../utils/countries';
import { PROVINCE_ITALIANE, COMUNI_ITALIANI, findCodiceComuneByName } from '../utils/comuniItaliani';
import { computeDataFinePreset, DATA_FINE_PRESETS, nowDatetimeLocal, selectMandatoPdf, selectSoggettoPdf, isGiornoSuccessivo, localIsoDate, nomeBaseFileMandato } from '../utils/utils';
import { normalizeFiscalCode, isValidFiscalCodeFormat } from '../utils/fiscalCode';
import { BONIFICA_FOOTER_PAGES } from '../components/BonificaPDF';
import { CONSULENZA_FOOTER_PAGES } from '../components/ConsulenzaPDF';
import { MANDATO_FOOTER_PAGES } from '../components/MandatoPDF';
import { pdf } from '@react-pdf/renderer';
import { InformativaPrivacyPDF } from '../components/InformativaPrivacyPDF';
import { stampInformativaPageNumbers } from '../utils/stampPageNumbers';
import { downloadAttachmentUrl } from '../utils/download';
import {
    fraseIncarichiAperti, ISTRUZIONI_INCARICHI_APERTI, totaleIncarichiAperti, IncarichiApertiConteggio,
} from '../utils/incarichiAperti';

interface Movimento {
    id: number;
    data: string;
    descrizione: string;
    importo: number;
    tipo: 'entrata' | 'uscita';
}

export const DettaglioPraticaView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { addToast } = useToast();
    const { user } = useAuth();
    const canEditRiferimento = user?.role === UserRole.ADMIN || user?.role === UserRole.RESPONSABILE;

    // Svuota dati soggetto investigato: solo titolare/responsabile, solo su mandati non più operativi.
    const STATI_SVUOTA_SOGGETTO = ['Sospesa', 'Conclusa', 'Archiviata', 'Annullata'];
    const [showSvuotaSoggetto, setShowSvuotaSoggetto] = useState(false);
    const [svuotaRelazione, setSvuotaRelazione] = useState(false);
    const [svuotaPdfSoggetto, setSvuotaPdfSoggetto] = useState(false);
    const [svuotaConferma, setSvuotaConferma] = useState('');
    const [isSvuotando, setIsSvuotando] = useState(false);

    // Dati
    const [pratica, setPratica] = useState<Pratica | null>(null);
    // Editor della relazione: web (HTML) o DOCX (OnlyOffice).
    const [showEditorChooser, setShowEditorChooser] = useState(false);
    // Scelta del soggetto nel chooser, solo con più soggetti (una relazione per
    // soggetto). 'html' | 'docx' = editor scelto.
    const [chooserSoggettoFor, setChooserSoggettoFor] = useState<null | 'html' | 'docx'>(null);
    // Modifica inline del riferimento dall'header.
    const [editingRif, setEditingRif] = useState(false);
    const [editingRifValue, setEditingRifValue] = useState('');
    const [savingRif, setSavingRif] = useState(false);
    const startEditRiferimentoHeader = () => {
        setEditingRifValue(((pratica as any)?.riferimento as string) || '');
        setEditingRif(true);
    };
    const cancelEditRiferimentoHeader = () => {
        setEditingRif(false);
        setEditingRifValue('');
    };
    const saveEditRiferimentoHeader = async () => {
        if (!pratica?.id) return;
        setSavingRif(true);
        try {
            const trimmed = editingRifValue.trim().slice(0, 120);
            const res: any = await PraticaService.patchRiferimento(Number(pratica.id), trimmed);
            const updated = res?.data?.riferimento ?? trimmed;
            setPratica({ ...pratica, riferimento: updated || null } as any);
            addToast("Riferimento aggiornato", "success");
            setEditingRif(false);
        } catch (e: any) {
            addToast(e?.response?.data?.message || "Errore aggiornamento riferimento", "error");
        } finally {
            setSavingRif(false);
        }
    };
    const [collaboratori, setCollaboratori] = useState<UserType[]>([]);
    const [incarichi, setIncarichi] = useState<Incarico[]>([]);
    const [settings, setSettings] = useState<any>(null);
    const [reports, setReports] = useState<any[]>([]);
    const [expandedVersionId, setExpandedVersionId] = useState<number | null>(null);

    const [showFirmaModalOTP, setShowFirmaModalOTP] = useState(false);
    // Secondo passo della firma, aperto solo dopo "Conferma e Invia". Come in
    // NuovaPraticaView, la scelta FEA resta fuori dal modale principale.
    const [isFirmaLoading, setIsFirmaLoading] = useState(false);
    const [showDatiMancantiModal, setShowDatiMancantiModal] = useState(false);
    const [showDatiIdentitaModal, setShowDatiIdentitaModal] = useState(false);
    const [datiIdentitaForm, setDatiIdentitaForm] = useState({
        dataNascita: '', docTipo: '', docNumero: '',
        docDataRilascio: '', docDataScadenza: '',
        indirizzo: '', provincia: '',
        codiceFiscale: '',
        docNazioneEmissione: 'IT',
        docProvinciaEmissione: '',
        docCittaEmissione: '',
        gender: '',
        cittadinanzaNazione: 'IT',
        nascitaNazione: 'IT',
        nascitaProvincia: '',
        nascitaComune: '',
        residenzaNazione: 'IT',
        residenzaComune: '',
        // Data di riconoscimento de visu (recognized_de_visu_on). Di default oggi,
        // modificabile se l'identificazione è avvenuta prima.
        recognizedDeVisuOn: '',
    });
    const [datiIdentitaFromClient, setDatiIdentitaFromClient] = useState<Set<string>>(new Set());

    // Cambio stato pratica
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [newStato, setNewStato] = useState('');
    const [showFatturaModal, setShowFatturaModal] = useState(false);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [forbidden, setForbidden] = useState(false);

    // Stato UI
    const [activeTab, setActiveTab] = useState('riepilogo');
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showAPosteriori, setShowAPosteriori] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    // Salvataggio in corso: blocca il doppio click su "Salva Registrazione".
    const [savingPayment, setSavingPayment] = useState(false);
    // Assegnazione in corso: blocca il doppio click su "Conferma".
    const [assigning, setAssigning] = useState(false);
    // Conclusione mandato in corso: blocca il doppio click (fattura registrata due volte).
    const [concludendo, setConcludendo] = useState(false);
    const [showAgendaSettimanale, setShowAgendaSettimanale] = useState(false);

    // Form assegnazione incarico
    const [assignForm, setAssignForm] = useState({
        collaboratoreId: '',
        dataInizio: '',
        soggettoNome: '',
        dataFine: '',
        luogo: '',
        note: '',
        // Scadenza di accettazione opzionale: se il collaboratore non accetta entro
        // la data, il backend porta l'incarico in RIFIUTATO.
        dataScadenzaAccettazione: '',
        // true se gli orari vengono dai tasti rapidi (Oggi/Domani/N giorni, fine 23:59):
        // il backend non segnala orari difformi, altrimenti ogni incarico chiuso prima
        // delle 23:59 risulterebbe fuori soglia. Torna false con una modifica manuale delle date.
        orariPreset: false,
        continuativo: false,
    });

    // Contabilità
    const [movimenti, setMovimenti] = useState<any[]>([]);
    const [budgetDati, setBudgetDati] = useState({ preventivo: 0, acconto: 0 });
    // Riepilogo economico calcolato dal BE alla chiusura del mandato, allineato al
    // tab Contabilità. È la base della modale "Registrazione Fattura", corretta
    // anche per i mandati a consuntivo.
    const [chiusuraRiepilogo, setChiusuraRiepilogo] = useState<{
        tipoCompenso: string;
        baseImponibile: number;
        baseIvato: number;
        totaleIncassato: number;
        saldoResiduo: number;
    } | null>(null);
    // Avviso prima della conclusione se restano incarichi aperti; con Conferma si prosegue
    // con il flusso normale (fattura o chiusura diretta), Annulla non cambia nulla.
    const [avvisoIncarichi, setAvvisoIncarichi] = useState<{
        conteggio: IncarichiApertiConteggio;
        riepilogo: Awaited<ReturnType<typeof PraticaService.getRiepilogoChiusura>>;
    } | null>(null);
    const [newPayment, setNewPayment] = useState({
        data: localIsoDate(),
        descrizione: '',
        importo: 0,
        tipo: 'entrata',
        // Come l'incasso si ripartisce tra imponibile e IVA.
        ivaMode: DEFAULT_INCASSO_MODE as IvaMode,
    });

    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyData, setHistoryData] = useState<any[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [showDownloadMenu, setShowDownloadMenu] = useState(false);

    const handleOpenHistory = async () => {
        setShowHistoryModal(true);
        setIsLoadingHistory(true);
        try {
            const res = await PraticaService.getHistory(Number(id));
            // Estrae l'array dalla risposta in modo tollerante
            const historyArray = Array.isArray(res) ? res : (res.data || []);
            setHistoryData(historyArray);
        } catch (error) {
            addToast("Impossibile caricare lo storico", "error");
        } finally {
            setIsLoadingHistory(false);
        }
    };
    const formatDatiKey = (key: string) => {
        return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const soggettiList = useMemo(() => {
        if (!pratica) return [];
        try {
            const dati = typeof (pratica as any).datiAggiuntiviJson === 'string'
                ? JSON.parse((pratica as any).datiAggiuntiviJson)
                : (pratica as any).datiAggiuntiviJson || {};

            if (dati.soggetti && Array.isArray(dati.soggetti)) return dati.soggetti;

            // Fallback per i mandati vecchi
            if (dati.soggetto_nome || dati.soggetto_denominazione) {
                return [{ nome: dati.soggetto_nome || dati.soggetto_denominazione }];
            }
        } catch (e) { console.error(e); }
        return [];
    }, [pratica]);

    // PDF della relazione archiviato dall'editor DOCX (categoria RELAZIONE_PDF, sempre
    // l'ultima versione). Uno per mandato o uno per soggetto
    // (Relazione_<id>_<n>.pdf, n = posizione del soggetto).
    const relazioniPdf = useMemo(() => ((pratica as any)?.files || [])
        .filter((f: any) => f?.categoria === 'RELAZIONE_PDF' || /^relazione_\d+(_[^.]+)?\.pdf$/i.test(f?.name || ''))
        .sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { numeric: true })),
    [pratica]);
    const labelRelazione = (f: any): string => {
        const m = /^relazione_\d+_(\d+)\.pdf$/i.exec(f?.name || '');
        if (!m) return 'Relazione';
        const nome = soggettiList[Number(m[1]) - 1]?.nome;
        return nome ? `Relazione · ${nome}` : `Relazione · soggetto ${m[1]}`;
    };
    const handleDownloadRelazione = async (f: any) => {
        setShowDownloadMenu(false);
        try {
            addToast("Generazione link di download...", "info");
            const response = await PraticaService.getDownloadUrl(f.id);
            const finalUrl = response?.url || response?.data?.url || (typeof response === 'string' ? response : null);
            if (finalUrl) downloadAttachmentUrl(finalUrl);
            else addToast("Il server non ha restituito un link valido.", "error");
        } catch (err) {
            console.error("Errore download relazione:", err);
            addToast("Impossibile scaricare la relazione. Riprova più tardi.", "error");
        }
    };

    // Cronologia: l'URL nello snapshot non è firmato (storage privato), quindi al
    // click chiediamo un link firmato al BE. Se il file non esiste più avvisiamo.
    const handleDownloadHistoryFile = async (file: any) => {
        if (!file?.id) return;
        try {
            const response = await PraticaService.getDownloadUrl(file.id);
            const finalUrl = response?.url || response?.data?.url || (typeof response === 'string' ? response : null);
            if (finalUrl) downloadAttachmentUrl(finalUrl);
            else addToast("Documento non più disponibile.", "error");
        } catch {
            addToast("Documento non più disponibile (eliminato o non accessibile).", "error");
        }
    };

    // Caricamento dati
    useEffect(() => {
        const fetchData = async () => {
            const numericId = Number(id);
            if (!id || isNaN(numericId)) {
                console.error("ID Pratica non valido:", id);
                return;
            }
            setLoading(true);
            try {
                // allSettled: per un mandato di una sede non gestita il dettaglio risponde 404 e le
                // liste 403, in ordine variabile. Si guarda l'esito di tutte le chiamate, così
                // l'accesso negato non dipende da quale risposta arriva per prima.
                const esiti = await Promise.allSettled([
                    PraticaService.getById(numericId),
                    IncaricoService.getByPratica(numericId, { includeAutogenerati: true }),
                    UserService.getAll(),
                    SettingsService.getAll(),
                    RelazioneGiornalieraService.getByPratica(numericId)
                ]);
                const falliti = esiti.filter((e): e is PromiseRejectedResult => e.status === 'rejected');
                if (falliti.length > 0) {
                    if (falliti.some(f => f.reason?.response?.status === 403)) {
                        const negato: any = new Error('forbidden');
                        negato.response = { status: 403 };
                        throw negato;
                    }
                    throw falliti[0].reason;
                }
                const [praticaRes, incarichiRes, usersRes, settingsRes, reportsRes] =
                    esiti.map(e => (e as PromiseFulfilledResult<any>).value) as any[];

                setPratica((praticaRes as any).data || praticaRes);

                const incarichiArr = Array.isArray(incarichiRes) ? incarichiRes : (incarichiRes as any).data || [];
                setIncarichi(incarichiArr);
                setCollaboratori(Array.isArray(usersRes) ? usersRes : (usersRes as any).data || []);

                const settingsData = (settingsRes as any).data || [];
                if (settingsData.length > 0) setSettings(settingsData[0]);

                const reportsArr = Array.isArray(reportsRes) ? reportsRes : reportsRes.data || [];
                reportsArr.sort((a: any, b: any) => {
                    const da = a.dataRelazione ? new Date(a.dataRelazione).getTime() : 0;
                    const db = b.dataRelazione ? new Date(b.dataRelazione).getTime() : 0;
                    return da - db;
                });
                setReports(reportsArr);

                const pData = (praticaRes as any).data || praticaRes;
                if ((pData as any).datiAggiuntiviJson) {
                    try {
                        const extra = JSON.parse((pData as any).datiAggiuntiviJson);
                        // Fallback su `compenso_concordato` per i template di consulenza (e altri vecchi)
                        // che non valorizzano `importo_preventivato`, come in TabContabilita e in
                        // AmministrazioneService.getBaseFatturazione.
                        const prevNum = Number(extra.importo_preventivato || extra.compenso_concordato || 0);
                        setBudgetDati({
                            preventivo: prevNum,
                            acconto: Number(extra.acconto || 0)
                        });
                        if (Array.isArray(extra.movimenti_contabili)) {
                            setMovimenti(extra.movimenti_contabili);
                        }
                    } catch (e) { }
                }
            } catch (err: any) {
                console.error(err);
                // 403: mandato di una sede non gestita. Il messaggio lo mostra già l'interceptor.
                if (err?.response?.status === 403) {
                    setForbidden(true);
                    return;
                }
                setError("Impossibile caricare i dettagli.");
                addToast("Errore caricamento dati", "error");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, addToast]);

    // Arrivando da NuovaPraticaView con `firma=precompila` ("Salva e precompila")
    // si apre subito il modale dati identità per compilare la FEA prima dell'invio.
    const [autoFirmaTriggered, setAutoFirmaTriggered] = useState(false);
    useEffect(() => {
        if (autoFirmaTriggered) return;
        if (!pratica) return;
        const firmaQS = searchParams.get('firma');
        if (firmaQS !== 'precompila') return;
        setAutoFirmaTriggered(true);
        // Tolgo subito il query param per non riattivare il flusso a ogni riapertura o reload.
        const next = new URLSearchParams(searchParams);
        next.delete('firma');
        setSearchParams(next, { replace: true });
        const email = pratica?.clientEmail;
        const tel   = pratica?.clientTelefono;
        if (!email || !tel) {
            setShowDatiMancantiModal(true);
            return;
        }
        // Carica i dati cliente e apre il form, saltando la scelta già fatta in NuovaPraticaView.
        handleAvviaFirmaOTP(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pratica, searchParams]);

    // Stato e avvio
    const handleApriModalFirma = () => {
        // clientEmail/clientTelefono arrivano già risolti dal PraticaDto: per le aziende
        // i recapiti del referente (ClientDisplayUtil.firmatarioEmail), per i privati
        // i campi nativi.
        const email = pratica?.clientEmail;
        const tel   = pratica?.clientTelefono;
        if (!email || !tel) {
            setShowDatiMancantiModal(true);
            return;
        }
        setShowFirmaModalOTP(true);
    };

    const doAvviaFirma = async (datiIdentita: typeof datiIdentitaForm | null, opts: { dryRun?: boolean } = {}) => {
        // PDF mandato/soggetto più recente non firmato (vedi selectPdfDaFirmare).
        const fileMandato  = selectMandatoPdf(pratica?.files);
        const fileSoggetto = selectSoggettoPdf(pratica?.files);
        // Bonifica e consulenza hanno un unico PDF con ancore proprie (#otp_b*#, #otp_c*#)
        // e nessun Dati Soggetto. Solo l'investigativo ha Mandato + Dati Soggetto
        // (#otp_m*# / #otp_s*#).
        const isInvestigativo = pratica?.modelId === 'mand-investigativo';
        setIsFirmaLoading(true);
        try {
            const buildFooterPunti = (n: number, prefix: string) =>
                Array.from({ length: n }, (_, i) => ({ key: `#otp_${prefix}${i + 1}#`, label: 'Firma qui', dynamic_position: true }));
            const _dati = (() => { try { const r = (pratica as any)?.datiAggiuntiviJson; return typeof r === 'string' ? JSON.parse(r) : r || {}; } catch { return {}; } })();
            const mandatoPageCount: number = _dati.mandatoPageCount || 12;
            const privacyPageCount: number = _dati.privacyPageCount || 0;

            // La privacy è accodata dentro il file del mandato; le ancore `#otp_pp{n}#` sono
            // in basso a destra di ogni sua pagina (InformativaPrivacyPDF in AUTO,
            // appendPrivacyPolicy in UPLOAD). Vale per tutti i tipi di mandato.
            const privacyPunti = privacyPageCount > 0 ? buildFooterPunti(privacyPageCount, 'pp') : [];

            // Punti firma del mandato per tipo, gli stessi di NuovaPraticaView.eseguiSalvataggio.
            let puntiMandato: any[];
            if (pratica?.modelId === 'prev-bonifiche') {
                puntiMandato = [
                    { key: '#otp_b1#', label: 'Firma Mandante', dynamic_position: true },
                    { key: '#otp_b2#', label: 'Approvazione Clausole art. 1341/1342 c.c.', dynamic_position: true },
                    // Limitiamo alle ancore presenti nel PDF: un overflow non aggiunge footer e
                    // chiedere ancore inesistenti fa rispondere OTP con 422 "Sign keys not found".
                    ...buildFooterPunti(Math.min(mandatoPageCount, BONIFICA_FOOTER_PAGES), 'bf'),
                    ...privacyPunti,
                ];
            } else if (pratica?.modelId === 'prev-consulenza') {
                puntiMandato = [
                    { key: '#otp_c1#', label: 'Firma Mandante', dynamic_position: true },
                    ...buildFooterPunti(Math.min(mandatoPageCount, CONSULENZA_FOOTER_PAGES), 'cf'),
                    ...privacyPunti,
                ];
            } else {
                // Investigativo (default)
                puntiMandato = [
                    { key: '#otp_m1#', label: 'Firma Mandante', dynamic_position: true },
                    { key: '#otp_m2#', label: 'Approvazione Clausole art. 1341/1342 c.c.', dynamic_position: true },
                    { key: '#otp_m3#', label: 'Dichiarazione Finale', dynamic_position: true },
                    ...buildFooterPunti(Math.min(mandatoPageCount, MANDATO_FOOTER_PAGES), 'mf'),
                    ...privacyPunti,
                ];
            }

            const payloadFirma: any = {
                mandato: {
                    fileId: fileMandato!.id,
                    puntiDiFirma: puntiMandato,
                },
                // Con datiIdentita il BE precompila tutti i campi FEA e OTP non chiede nulla
                // al cliente; con null li compila il cliente durante la firma.
                prefill_dati_identita: datiIdentita !== null,
                ...(datiIdentita ? { dati_identita: {
                    data_nascita:        datiIdentita.dataNascita,
                    doc_tipo:            datiIdentita.docTipo,
                    doc_numero:          datiIdentita.docNumero,
                    doc_data_rilascio:   datiIdentita.docDataRilascio,
                    doc_data_scadenza:   datiIdentita.docDataScadenza,
                    indirizzo:           datiIdentita.indirizzo,
                    provincia:           datiIdentita.provincia,
                    codice_fiscale:      datiIdentita.codiceFiscale,
                    doc_nazione_emissione:  datiIdentita.docNazioneEmissione,
                    doc_provincia_emissione: datiIdentita.docProvinciaEmissione,
                    doc_citta_emissione:    datiIdentita.docCittaEmissione,
                    gender:                  datiIdentita.gender,
                    cittadinanza_nazione:    datiIdentita.cittadinanzaNazione,
                    nascita_nazione:         datiIdentita.nascitaNazione,
                    nascita_provincia:       datiIdentita.nascitaProvincia,
                    nascita_comune:          datiIdentita.nascitaComune,
                    residenza_nazione:       datiIdentita.residenzaNazione,
                    residenza_comune:        datiIdentita.residenzaComune,
                    recognized_de_visu_on:   datiIdentita.recognizedDeVisuOn || null,
                }} : {}),
            };

            // Dati Soggetto solo per i mandati investigativi.
            if (isInvestigativo && fileSoggetto) {
                // Il PDF Dati Soggetto ha sempre 3 pagine per soggetto, con una firma finale
                // (#otp_s{i}#) e un footer #otp_sf{n}# su ogni pagina. I soggetti si contano da
                // soggettiList e non da soggettoPageCount, che può includere le pagine della
                // privacy e farebbe chiedere ancore sf inesistenti.
                const numSoggetti = Math.max(1, soggettiList.length);
                const soggettoFooterPages = numSoggetti * 3;
                const puntiFinaliSoggetto = Array.from({ length: numSoggetti }, (_, i) => ({
                    key: `#otp_s${i + 1}#`,
                    label: numSoggetti > 1 ? `Firma Soggetto Investigato ${i + 1}` : 'Firma Soggetto Investigato',
                    dynamic_position: true,
                }));
                payloadFirma.datiSoggetto = {
                    fileId: fileSoggetto.id,
                    puntiDiFirma: [
                        ...puntiFinaliSoggetto,
                        ...buildFooterPunti(soggettoFooterPages, 'sf'),
                        // Nessuna ancora privacy sul Dati Soggetto: l'informativa è solo sul mandato.
                    ]
                };
            }

            if (opts.dryRun) {
                // Debug: endpoint dry-run che non crea nulla su OTP. Logga il RecipientDto e
                // prova a copiarlo negli appunti.
                const dryRes = await PraticaService.avviaFirmaOTPDryRun(Number(pratica?.id), payloadFirma);
                const recipient = dryRes?.data ?? dryRes;
                console.group('[DRY-RUN avvia-firma] RecipientDto che verrebbe inviato a OTP');
                console.log(recipient);
                console.log('recognized_de_visu_on =', recipient?.recognized_de_visu_on);
                console.groupEnd();
                const json = JSON.stringify(recipient, null, 2);
                try { await navigator.clipboard.writeText(json); } catch {}
                const rdv = recipient?.recognized_de_visu_on;
                addToast(`DRY-RUN: recognized_de_visu_on = ${rdv === undefined ? 'OMESSO ✓' : JSON.stringify(rdv)}. JSON copiato in console + clipboard.`, rdv === undefined ? 'success' : 'error');
                return;
            }
            await PraticaService.avviaFirmaOTP(Number(pratica?.id), payloadFirma);
            addToast("Processo di firma avviato con successo! Il cliente riceverà una email da OTP Service.", "success");
            setShowFirmaModalOTP(false);
            setShowDatiIdentitaModal(false);
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || "Errore durante l'avvio della firma";
            addToast(msg, "error");
        } finally {
            setIsFirmaLoading(false);
        }
    };

    /**
     * Avvio della firma dopo la scelta nel modale `showFirmaModalOTP`.
     *
     * @param prefill se true carica i dati del cliente e apre il form FEA da compilare
     * dall'operatore; se false invia subito a OTP e i campi li compila il cliente.
     */
    const handleAvviaFirmaOTP = async (prefill: boolean) => {
        // PDF mandato/soggetto più recente non firmato (vedi selectPdfDaFirmare).
        const fileMandato  = selectMandatoPdf(pratica?.files);
        const fileSoggetto = selectSoggettoPdf(pratica?.files);
        const isInvestigativo = pratica?.modelId === 'mand-investigativo';

        if (!fileMandato) {
            addToast("Impossibile avviare: il file PDF del mandato non è presente.", "error");
            return;
        }
        // Solo l'investigativo firma anche il Dati Soggetto; bonifica e consulenza solo il mandato.
        if (isInvestigativo && !fileSoggetto) {
            addToast("Impossibile avviare: il file PDF della scheda soggetto non è presente.", "error");
            return;
        }

        if (prefill) {
            try {
                const clientRes = await ClientService.getById((pratica as any).clientId);
                const cliente = (clientRes as any).data || clientRes;
                // Le date del Client possono essere GG/MM/YYYY (vecchi form) o ISO;
                // <input type="date"> vuole YYYY-MM-DD.
                const toIsoDate = (raw: any): string => {
                    if (!raw || typeof raw !== 'string') return '';
                    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
                    const m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                    return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
                };
                const fromClient = new Set<string>();
                const form = {
                    dataNascita: '', docTipo: '', docNumero: '',
                    docDataRilascio: '', docDataScadenza: '',
                    indirizzo: '', provincia: '',
                    codiceFiscale: '',
                    docNazioneEmissione: 'IT',
                    docProvinciaEmissione: '',
                    docCittaEmissione: '',
                    gender: '',
                    cittadinanzaNazione: 'IT',
                    nascitaNazione: 'IT',
                    nascitaProvincia: '',
                    nascitaComune: '',
                    residenzaNazione: 'IT',
                    residenzaComune: '',
                    // Per i mandati investigativi il riconoscimento è sempre a distanza
                    // (foto documento + selfie): nessuna data de visu.
                    recognizedDeVisuOn: '', // Mai di persona: sempre foto del documento
                };
                if (cliente.dataNascita)     { form.dataNascita = toIsoDate(cliente.dataNascita);         if (form.dataNascita)     fromClient.add('dataNascita'); }
                if (cliente.docTipo)         { form.docTipo = cliente.docTipo;                            fromClient.add('docTipo'); }
                if (cliente.docNumero)       { form.docNumero = cliente.docNumero;                        fromClient.add('docNumero'); }
                if (cliente.docDataRilascio) { form.docDataRilascio = toIsoDate(cliente.docDataRilascio); if (form.docDataRilascio) fromClient.add('docDataRilascio'); }
                if (cliente.docDataScadenza) { form.docDataScadenza = toIsoDate(cliente.docDataScadenza); if (form.docDataScadenza) fromClient.add('docDataScadenza'); }
                if (cliente.indirizzo)       { form.indirizzo = cliente.indirizzo;                        fromClient.add('indirizzo'); }
                if (cliente.provincia)       { form.provincia = cliente.provincia;                        fromClient.add('provincia'); }
                if (cliente.codiceFiscale)   { form.codiceFiscale = cliente.codiceFiscale;                fromClient.add('codiceFiscale'); }
                setDatiIdentitaForm(form);
                setDatiIdentitaFromClient(fromClient);
                setShowFirmaModalOTP(false);
                setShowDatiIdentitaModal(true);
            } catch {
                addToast("Impossibile caricare i dati del cliente. Riprova.", "error");
            }
            return;
        }

        await doAvviaFirma(null);
    };

    const handleConfirmDatiIdentita = async (opts: { dryRun?: boolean } = {}) => {
        const missing: string[] = [];
        if (!datiIdentitaForm.codiceFiscale)      missing.push('Codice fiscale');
        if (!datiIdentitaForm.dataNascita)        missing.push('Data di nascita');
        if (!datiIdentitaForm.gender)             missing.push('Sesso');
        if (!datiIdentitaForm.cittadinanzaNazione) missing.push('Cittadinanza');
        if (!datiIdentitaForm.nascitaNazione)     missing.push('Nazione di nascita');
        if (datiIdentitaForm.nascitaNazione === 'IT') {
            if (!datiIdentitaForm.nascitaProvincia) missing.push('Provincia di nascita');
            if (!datiIdentitaForm.nascitaComune)    missing.push('Comune di nascita');
        }
        if (!datiIdentitaForm.docTipo)            missing.push('Tipo documento');
        if (!datiIdentitaForm.docNumero)          missing.push('Numero documento');
        if (!datiIdentitaForm.docDataRilascio)    missing.push('Data rilascio');
        if (!datiIdentitaForm.docDataScadenza)    missing.push('Data scadenza');
        if (!datiIdentitaForm.docNazioneEmissione) missing.push('Nazione emissione documento');
        // Provincia e comune di emissione obbligatori solo per documenti italiani
        // (per gli stranieri OTP chiede di ometterli).
        if (datiIdentitaForm.docNazioneEmissione === 'IT') {
            if (!datiIdentitaForm.docProvinciaEmissione) missing.push('Provincia emissione documento');
            if (!datiIdentitaForm.docCittaEmissione)     missing.push('Comune emissione documento');
        }
        if (!datiIdentitaForm.residenzaNazione)   missing.push('Nazione di residenza');
        // Provincia, comune e indirizzo obbligatori solo per residenti in Italia
        // (per gli stranieri OTP chiede di ometterli).
        if (datiIdentitaForm.residenzaNazione === 'IT') {
            if (!datiIdentitaForm.provincia)       missing.push('Provincia di residenza');
            if (!datiIdentitaForm.residenzaComune) missing.push('Comune di residenza');
            if (!datiIdentitaForm.indirizzo)       missing.push('Indirizzo residenza');
        }
        if (missing.length > 0) {
            addToast(`Compila i campi obbligatori: ${missing.join(', ')}`, "error");
            return;
        }
        // Validazione del CF con la stessa regex del backend, per evitare un giro
        // inutile su OTP. normalizeFiscalCode corregge gli scambi O↔0 e I↔1 dove
        // il tipo di carattere è certo.
        const cfNorm = normalizeFiscalCode(datiIdentitaForm.codiceFiscale);
        if (!isValidFiscalCodeFormat(cfNorm)) {
            if (cfNorm.length !== 16) {
                addToast(`Codice fiscale non valido: '${cfNorm}'. Deve essere di 16 caratteri.`, "error");
            } else {
                addToast(`Codice fiscale non valido: '${cfNorm}'. Controlla che il formato sia lettere/cifre nelle posizioni corrette.`, "error");
            }
            return;
        }
        // Se la normalizzazione ha cambiato il valore aggiorno il form, così l'utente
        // vede quello corretto.
        if (cfNorm !== datiIdentitaForm.codiceFiscale) {
            setDatiIdentitaForm(f => ({ ...f, codiceFiscale: cfNorm }));
        }
        await doAvviaFirma({ ...datiIdentitaForm, codiceFiscale: cfNorm }, { dryRun: opts.dryRun });
    };

    const handleDownloadFirmato = async () => {
        const dossierId = (pratica as any)?.otpMandatoDossierId;
        if (!dossierId) { addToast("ID dossier non disponibile", "error"); return; }
        try {
            const nomePratica = pratica?.name?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'pratica';
            await PraticaService.downloadMandatoFirmato(dossierId, `mandato_firmato_${nomePratica}.pdf`);
        } catch (e) {
            addToast("Errore durante il download del mandato firmato", "error");
        }
    };

    const handleAttivaLavoro = async () => {
        if (!window.confirm("Sei sicuro di voler attivare questo mandato e passarlo 'In Corso'?")) return;

        try {
            await PraticaService.attivaPratica(Number(pratica?.id));
            addToast("Mandato attivato con successo! Ora è In Corso.", "success");
            setPratica({ ...pratica!, stato: "In Corso" });
        } catch (e) {
            addToast("Errore durante l'attivazione del mandato", "error");
        }
    };

    const handleCambiaStato = async () => {
        if (!newStato) return;
        if (newStato === 'Conclusa') {
            // Chiede al BE il riepilogo economico come nel tab Contabilità, con i conteggi
            // degli incarichi ancora aperti.
            let riepilogo: Awaited<ReturnType<typeof PraticaService.getRiepilogoChiusura>>;
            try {
                riepilogo = await PraticaService.getRiepilogoChiusura(Number(pratica?.id));
            } catch (e) {
                // Se il BE non risponde mostriamo comunque la modale
                console.warn('[chiusura] riepilogo BE fallito, mostro modal senza dati BE:', e);
                setChiusuraRiepilogo(null);
                setShowStatusModal(false);
                setShowFatturaModal(true);
                return;
            }
            if (totaleIncarichiAperti(riepilogo) > 0) {
                setShowStatusModal(false);
                setAvvisoIncarichi({ conteggio: riepilogo, riepilogo });
                return;
            }
            await proseguiConclusione(riepilogo);
            return;
        }
        try {
            await PraticaService.cambiaStato(Number(pratica?.id), newStato);
            setPratica({ ...pratica!, stato: newStato });
            addToast(`Stato della pratica aggiornato a: ${newStato}`, "success");
            setShowStatusModal(false);
        } catch (e) {
            addToast("Errore durante l'aggiornamento dello stato", "error");
        }
    };

    // Seconda parte della conclusione, dopo l'eventuale avviso sugli incarichi aperti:
    // senza nulla da riscuotere chiude il mandato, altrimenti propone la registrazione fattura.
    const proseguiConclusione = async (riepilogo: Awaited<ReturnType<typeof PraticaService.getRiepilogoChiusura>>) => {
        setChiusuraRiepilogo(riepilogo);
        setShowStatusModal(false);
        if (!riepilogo.saldoResiduo || riepilogo.saldoResiduo < 0.005) {
            try {
                await PraticaService.cambiaStato(Number(pratica?.id), 'Conclusa');
                setPratica({ ...pratica!, stato: 'Conclusa' });
                addToast('Mandato concluso. Nulla da riscuotere.', 'success');
            } catch (e) {
                addToast("Errore durante l'aggiornamento dello stato", "error");
            }
            return;
        }
        setShowFatturaModal(true);
    };

    const handleConfermaConclusioneConOpzione = async (opzione: 'totale' | 'nessuna') => {
        // L'importo della fattura è il saldo residuo, non il totale: incassi e acconti
        // sono già registrati come entrate e verrebbero contati due volte.
        // Se manca il riepilogo del BE si usa un calcolo semplice lato FE.
        const fallbackSaldo = Math.max(
            0,
            Math.round(budgetDati.preventivo * 1.22 * 100) / 100
                - movimenti.filter(m => m.tipo === 'entrata').reduce((acc, m) => acc + Number(m.importo), 0),
        );
        const importoDaRiscuotere = chiusuraRiepilogo?.saldoResiduo ?? fallbackSaldo;
        if (concludendo) return;
        setConcludendo(true);
        try {
            await PraticaService.cambiaStato(Number(pratica?.id), 'Conclusa');
            let fatturaRegistrata = false;
            if (opzione !== 'nessuna') {
                if (importoDaRiscuotere > 0) {
                    try {
                        await PraticaService.registraFattura(Number(pratica?.id), {
                            importoFatturato: importoDaRiscuotere,
                        });
                        fatturaRegistrata = true;
                        addToast("Mandato concluso e fattura registrata.", "success");
                    } catch {
                        // Il mandato è già concluso: va detto, altrimenti si riprova e si conclude due volte.
                        addToast("Mandato concluso, ma la fattura non è stata registrata: registrala dall'amministrazione.", "error");
                    }
                } else {
                    addToast("Mandato concluso. Nulla da fatturare.", "info");
                }
            } else {
                addToast("Mandato concluso senza fattura. Ricordati di registrarla.", "info");
            }

            // Ricarica la pratica dopo che il BE ha registrato la fattura nel JSON, altrimenti
            // il tab Contabilità mostrerebbe la lista vecchia.
            if (fatturaRegistrata) {
                try {
                    const refreshed: any = await PraticaService.getById(Number(pratica?.id));
                    const pData = refreshed?.data || refreshed;
                    setPratica(pData);
                    if (pData?.datiAggiuntiviJson) {
                        try {
                            const extra = JSON.parse(pData.datiAggiuntiviJson);
                            if (Array.isArray(extra.movimenti_contabili)) {
                                setMovimenti(extra.movimenti_contabili);
                            }
                        } catch { /* JSON non valido, resta lo stato precedente */ }
                    }
                } catch {
                    // Se il refresh fallisce aggiorniamo almeno lo stato locale, per non restare su "In Corso".
                    setPratica({ ...pratica!, stato: 'Conclusa' });
                }
            } else {
                setPratica({ ...pratica!, stato: 'Conclusa' });
            }
            setShowFatturaModal(false);
        } catch (e) {
            addToast("Errore: il mandato non è stato concluso", "error");
        } finally {
            setConcludendo(false);
        }
    };

    const getStatusColor = (stato: string) => {
        const s = (stato || '').toUpperCase();
        if (s.includes('CORSO')) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
        if (s.includes('CONCLUS') || s.includes('COMPLETAT')) return 'bg-gray-200 text-gray-700 border-gray-300';
        if (s.includes('SOSPES')) return 'bg-amber-100 text-amber-800 border-amber-200';
        if (s.includes('ANNULLAT')) return 'bg-rose-100 text-rose-800 border-rose-200';
        if (s.includes('ATTESA')) return 'bg-blue-100 text-blue-800 border-blue-200';
        return 'bg-slate-100 text-slate-700 border-slate-200';
    };

    // Calcoli e media.
    // Ricarica i report dal server, chiamato da TabReport dopo un'approvazione
    const refreshReports = async () => {
        try {
            const res = await RelazioneGiornalieraService.getByPratica(Number(id));
            const reportsArr = Array.isArray(res) ? res : (res as any).data || [];
            reportsArr.sort((a: any, b: any) => {
                const da = a.dataRelazione ? new Date(a.dataRelazione).getTime() : 0;
                const db = b.dataRelazione ? new Date(b.dataRelazione).getTime() : 0;
                return da - db;
            });
            setReports(reportsArr);
        } catch (e) {
            console.error('Errore refresh reports:', e);
        }
    };

    const refreshIncarichi = async () => {
        try {
            // Ricarica pratica e incarichi: TabMedia mostra sia pratica.files (upload ufficio)
            // sia incarichi[].media, quindi serve aggiornare entrambi.
            const numericId = Number(id);
            const [praticaRes, incarichiRes] = await Promise.all([
                PraticaService.getById(numericId),
                IncaricoService.getByPratica(numericId, { includeAutogenerati: true }),
            ]);
            const newPratica = (praticaRes as any).data || praticaRes;
            if (newPratica) setPratica(newPratica);
            setIncarichi(Array.isArray(incarichiRes) ? incarichiRes : (incarichiRes as any).data || []);
        } catch (e) {
            console.error('Errore refresh:', e);
        }
    };

    // Gli AUTOGENERATO sono incarichi di appoggio per i media caricati da
    // TabMedia/FileManager. Restano in `incarichi` perché TabMedia li legge, ma
    // sono esclusi da TabIncarichi, costi collaboratori e statistiche team.
    const incarichiOperativi = useMemo(
        () => incarichi.filter(inc => (inc as any).tipoOrigine !== 'AUTOGENERATO'),
        [incarichi],
    );

    // Costi collaboratore (teamStats), separati dalla fatturazione al cliente che sta
    // in TabContabilita. Usa costoOrario e rimborsoKm del singolo collaboratore.
    const teamStats = useMemo(() => {
        const stats: Record<number, any> = {};
        const fallbackTariffaKm = settings?.priceKM || 0;

        incarichiOperativi.forEach(inc => {
            if (!inc.collaboratore) return;
            const uid = inc.collaboratore.id;
            if (!stats[uid]) {
                stats[uid] = {
                    user: inc.collaboratore,
                    incarichiCount: 0,
                    totalOre: 0,
                    totalKm: 0,
                    totalSpese: 0,
                    costoStimato: 0
                };
            }
            stats[uid].incarichiCount++;
        });

        reports.filter(r => r.stato === 'APPROVATO').forEach(report => {
            if (!report.collaboratore) return;
            const uid = report.collaboratore.id;
            if (!stats[uid]) {
                stats[uid] = {
                    user: report.collaboratore,
                    incarichiCount: 0,
                    totalOre: 0,
                    totalKm: 0,
                    totalSpese: 0,
                    costoStimato: 0
                };
            }
            // Ore approvate: override del titolare se presente, altrimenti GPS + extra
            // approvate (come in AmministrazioneService).
            stats[uid].totalOre += report.oreApprovateTotali != null
                ? Number(report.oreApprovateTotali)
                : (report.oreGps || 0) + (report.oreExtraApprovate || 0);
            // Km: solo auto propria
            if (!report.autoAziendale) {
                stats[uid].totalKm += (report.kmApprovati || 0);
            }
            // Spese: escluse carta aziendale
            const speseList = report.speseEspanso || report.spese || [];
            stats[uid].totalSpese += speseList
                .filter((s: any) => !s.anticipatoAzienda)
                .reduce((acc: number, s: any) => acc + (s.importoApprovato ?? s.importo ?? 0), 0);
        });

        return Object.values(stats).map((stat: any) => {
            const costoOrario: number = stat.user.costoOrario || settings?.hourlyRate || 0;
            const rimborsoKm: number  = stat.user.rimborsoKm  || fallbackTariffaKm;
            const costoTotale = stat.totalOre * costoOrario + stat.totalKm * rimborsoKm + stat.totalSpese;
            return {
                ...stat,
                costoOrario,
                rimborsoKm,
                costoStimato: costoTotale,
                displayTime: {
                    h: Math.floor(stat.totalOre),
                    m: Math.round((stat.totalOre % 1) * 60)
                }
            };
        });
    }, [incarichiOperativi, reports, settings, pratica]);


    // Handlers
    const handleDownloadDoc = async (kind: 'mandato' | 'soggetto' | 'privacy') => {
        setShowDownloadMenu(false);

        // Informativa privacy: è il documento aziendale, lo stesso scaricabile da
        // Impostazioni, non un file della pratica.
        // - privacyPolicyMode === 'AUTO' → generata da InformativaPrivacyPDF
        // - altrimenti (UPLOAD) → settings.privacyPolicyUrl
        if (kind === 'privacy') {
            if (!settings) {
                addToast("Impostazioni non ancora caricate. Riprova tra un istante.", "error");
                return;
            }
            try {
                if (settings.privacyPolicyMode === 'AUTO') {
                    addToast("Generazione Informativa Privacy...", "info");
                    const blob = await stampInformativaPageNumbers(
                        await pdf(<InformativaPrivacyPDF settings={settings as any} />).toBlob()
                    );
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `Informativa_Privacy_${(settings.agencyName || 'Agenzia').replace(/\s+/g, '_')}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                } else if ((settings as any).privacyPolicyUrl) {
                    window.open((settings as any).privacyPolicyUrl, '_blank');
                } else {
                    addToast("Nessuna Informativa Privacy configurata nelle Impostazioni.", "error");
                }
            } catch (err) {
                console.error("Errore download Informativa Privacy:", err);
                addToast("Impossibile generare l'Informativa Privacy.", "error");
            }
            return;
        }

        // Se il documento è già firmato via OTP scarichiamo la versione firmata
        // (/signed-pdf) invece del PDF archiviato su S3:
        // - "mandato"  → otpMandatoDossierId, se stato MANDATO_FIRMATO o FIRMATA
        // - "soggetto" → otpPrivacyDossierId, se stato PRIVACY_FIRMATA o FIRMATA
        // (vedi Pratica.otpFirmaStatus).
        const status = pratica?.otpFirmaStatus;
        const signedDossierId = kind === 'mandato'
            ? (pratica as any)?.otpMandatoDossierId
            : (pratica as any)?.otpPrivacyDossierId;
        const isSigned = !!signedDossierId && (
            kind === 'mandato'
                ? (status === 'MANDATO_FIRMATO' || status === 'FIRMATA')
                : (status === 'SOGGETTO_FIRMATO' || status === 'PRIVACY_FIRMATA' || status === 'FIRMATA')
        );

        if (isSigned) {
            try {
                addToast("Scaricamento documento firmato...", "info");
                // Nr. Mandato (o id), mai il nome del soggetto o del cliente: come i PDF non firmati.
                const dati = (() => { try { const r = (pratica as any)?.datiAggiuntiviJson; return typeof r === 'string' ? JSON.parse(r) : r || {}; } catch { return {}; } })();
                const base = nomeBaseFileMandato(dati?.rif_numero_pratica, pratica?.id != null ? Number(pratica.id) : null);
                const filename = kind === 'mandato'
                    ? `Mandato_Firmato_${base}.pdf`
                    : `DatiSoggetto_Firmato_${base}.pdf`;
                await PraticaService.downloadMandatoFirmato(Number(signedDossierId), filename);
                return;
            } catch (err) {
                console.error("Errore download firmato, fallback a pre-firma:", err);
                addToast("Impossibile scaricare la versione firmata, recupero la pre-firma...", "info");
                // Si prosegue con il PDF non firmato su S3
            }
        }

        if (!pratica?.files?.length) {
            addToast("Nessun documento presente.", "error");
            return;
        }

        // Fallback: PDF archiviato su S3 alla creazione/modifica, salvato come
        // "Mandato_<nome>.pdf" / "DatiSoggetto_<nome>.pdf". Confronto in minuscolo.
        const needle = kind === 'mandato' ? 'mandato_' : 'datisoggetto_';
        const labelMissing = kind === 'mandato' ? 'Mandato' : 'Soggetto investigato';

        const docFile = [...pratica.files]
            .filter(f => f.name.toLowerCase().includes(needle) && f.extension.toLowerCase() === "pdf")
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

        if (!docFile || docFile.id === undefined) {
            addToast(`${labelMissing} non trovato.`, "error");
            return;
        }

        try {
            addToast("Generazione link di download...", "info");

            const response = await PraticaService.getDownloadUrl(docFile.id);

            // La risposta può essere { url } oppure { data: { url } }
            const finalUrl = response?.url || response?.data?.url || (typeof response === 'string' ? response : null);

            if (finalUrl) {
                downloadAttachmentUrl(finalUrl);
            } else {
                console.error("Risposta inattesa dal server:", response);
                addToast("Il server non ha restituito un link valido.", "error");
            }
        } catch (err) {
            console.error("Errore download:", err);
            addToast("Impossibile rigenerare il link S3. Riprova più tardi.", "error");
        }
    };

    useEffect(() => {
        if (showAssignModal && soggettiList.length === 1) {
            setAssignForm(prev => ({ ...prev, soggettoNome: soggettiList[0].nome }));
        }
    }, [showAssignModal, soggettiList]);

    // Validazioni del form Assegna Incarico, per bloccare il submit e per i messaggi inline.
    const assignMissingSoggetto = soggettiList.length >= 2 && !assignForm.soggettoNome;
    const assignDataFineInvalida = !!assignForm.dataInizio && !!assignForm.dataFine
        && new Date(assignForm.dataFine).getTime() < new Date(assignForm.dataInizio).getTime();
    // dataFine obbligatoria: il backend divide i periodi di più giorni in incarichi
    // giornalieri e serve un estremo finale.
    // Checkbox continuativo visibile solo se la fine cade il giorno dopo l'inizio.
    const assignContinuativoEligible = isGiornoSuccessivo(assignForm.dataInizio, assignForm.dataFine);
    const assignCanConfirm = !!assignForm.collaboratoreId && !!assignForm.dataInizio
        && !!assignForm.dataFine && !assignMissingSoggetto && !assignDataFineInvalida;

    // Conflitti di disponibilità restituiti dopo la creazione (collaboratore già
    // impegnato in turni o incarichi sovrapposti). Se presenti si apre la modale.
    const [conflittiAssegnazione, setConflittiAssegnazione] = useState<ConflittoDisponibilita[]>([]);
    const [conflittiSummary, setConflittiSummary] = useState<{ collab?: string; creati: number; totale: number }>({ creati: 0, totale: 0 });
    // Payload dell'ultima assegnazione, per reinviare la POST con forceOverride=true
    // se dalla modale conflitti il titolare conferma i conflitti `overridable`
    // (collaboratore che ha fatto stop). Si azzera alla chiusura della modale.
    const [pendingAssignPayload, setPendingAssignPayload] = useState<any | null>(null);

    const submitAssign = async (forceOverride: boolean) => {
        const payload = forceOverride && pendingAssignPayload
            ? { ...pendingAssignPayload, forceOverride: true }
            : {
                praticaId: Number(id),
                collaboratoreId: Number(assignForm.collaboratoreId),
                soggettoNome: assignForm.soggettoNome,
                dataInizio: assignForm.dataInizio,
                dataFine: assignForm.dataFine,
                luogo: assignForm.luogo,
                note: assignForm.note,
                dataScadenzaAccettazione: assignForm.dataScadenzaAccettazione || null,
                orariPreset: assignForm.orariPreset,
                continuativo: !!assignForm.continuativo && assignContinuativoEligible,
            };
        const res = await IncaricoService.create(payload);
        const data: any = (res as any)?.data || {};
        const created: any[] = Array.isArray(data?.created) ? data.created : (Array.isArray(data) ? data : []);
        const conflicts: ConflittoDisponibilita[] = Array.isArray(data?.conflicts) ? data.conflicts : [];

        if (conflicts.length > 0) {
            const collab: any = (collaboratori as any[]).find((u: any) => String(u.id) === String(payload.collaboratoreId));
            setConflittiSummary({
                collab: collab ? `${collab.name} ${collab.surname || ''}`.trim() : undefined,
                creati: created.length,
                totale: created.length + conflicts.length,
            });
            setConflittiAssegnazione(conflicts);
            // Teniamo il payload solo se non abbiamo già forzato e ci sono conflitti
            // sovrascrivibili. Dopo un invio forzato un residuo vuol dire che c'è almeno un
            // conflitto non sovrascrivibile: reinviare darebbe lo stesso esito e
            // l'incarico appena creato diventerebbe un nuovo conflitto.
            const someOverridable = !forceOverride && conflicts.some(c => (c as any).overridable);
            setPendingAssignPayload(someOverridable ? payload : null);
            addToast(res?.message || `Assegnati ${created.length} incarichi; ${conflicts.length} saltati per conflitto.`, 'info');
        } else {
            setPendingAssignPayload(null);
            addToast(res?.message || (forceOverride ? "Incarico assegnato con override" : "Incarico assegnato"), "success");
        }
        if (!forceOverride) {
            setShowAssignModal(false);
            setAssignForm({ collaboratoreId: '', soggettoNome: '', dataInizio: '', dataFine: '', luogo: '', note: '', dataScadenzaAccettazione: '', orariPreset: false, continuativo: false });
        }
        // L'assegnazione è già salvata: se fallisce solo il ricaricamento non va mostrato
        // "Errore assegnazione", altrimenti l'utente riprova e crea un incarico doppio.
        try {
            const updated = await IncaricoService.getByPratica(Number(id), { includeAutogenerati: true });
            setIncarichi(Array.isArray(updated) ? updated : (updated as any).data || []);
        } catch {
            addToast("Incarico assegnato, ma l'elenco non si è aggiornato: ricarica la pagina", 'info');
        }
    };

    const handleAssignSubmit = async () => {
        if (!assignForm.collaboratoreId || !assignForm.dataInizio || !assignForm.dataFine) {
            return addToast("Compila collaboratore, data inizio e data fine", "error");
        }
        if (assignMissingSoggetto) return addToast("Seleziona il soggetto investigato", "error");
        if (assignDataFineInvalida) return addToast("La data di fine deve essere uguale o successiva alla data di inizio", "error");
        if (assigning) return;
        setAssigning(true);
        try {
            await submitAssign(false);
        } catch { addToast("Errore assegnazione", "error"); }
        finally { setAssigning(false); }
    };

    // "Procedi comunque" dalla modale conflitti: reinvia la POST con forceOverride=true.
    // Il backend salva le tranche con conflitti tutti sovrascrivibili e restituisce
    // in `conflicts` solo quelli rimasti.
    const handleConfermaOverride = async () => {
        if (assigning) return;
        setConflittiAssegnazione([]);
        setAssigning(true);
        try { await submitAssign(true); }
        catch { addToast("Errore durante l'override", "error"); }
        finally { setAssigning(false); }
    };

    const handleRevocaIncarico = async (incaricoId: number) => {
        // Conferma dettagliata: mostra cosa resterà dopo la revoca (foto, report, note),
        // con i conteggi già presenti nell'IncaricoDto.
        const inc = incarichi.find((x: any) => x.id === incaricoId) as any;
        const nMedia = inc?.media?.length || 0;
        const nReport = inc?.relazioni?.length || 0;
        const nNote = inc?.noteList?.length || 0;
        const lines = [
            `Revoca questo incarico?`,
            ``,
            `Il collaboratore non potrà più lavorarci, ma resta tutto consultabile:`,
            `• ${nMedia} foto/video caricati`,
            `• ${nReport} report giornalieri trasmessi`,
            `• ${nNote} note operative`,
            ``,
            `Le spese in attesa di approvazione restano in coda.`,
        ];
        if (!window.confirm(lines.join('\n'))) return;
        try {
            await IncaricoService.revoca(incaricoId);
            addToast("Incarico revocato con successo", "success");
            const updated = await IncaricoService.getByPratica(Number(id), { includeAutogenerati: true });
            setIncarichi(Array.isArray(updated) ? updated : (updated as any).data || []);
        } catch (err: any) {
            console.error(err);
            // Con report DA_REVISIONARE il backend risponde 409 con un messaggio chiaro:
            // lo mostriamo così il titolare sa cosa fare prima di revocare.
            const status = err?.response?.status;
            const msg = err?.response?.data?.message;
            if (status === 409 && msg) {
                addToast(msg, "info");
            } else {
                addToast("Errore durante la revoca dell'incarico", "error");
            }
        }
    };

    const handleDeleteIncarico = async (incaricoId: number) => {
        if (!window.confirm("Eliminare definitivamente questo incarico? L'operazione non è reversibile ed è consentita solo se non ci sono sessioni né prove caricate.")) return;
        try {
            await IncaricoService.delete(incaricoId);
            addToast("Incarico eliminato con successo", "success");
            setIncarichi(prev => prev.filter(inc => inc.id !== incaricoId));
        } catch (err: any) {
            console.error(err);
            // 409: incarico già avviato o con prove caricate, non eliminabile; il BE
            // suggerisce la revoca. Come per la revoca e per eliminaTurno.
            const status = err?.response?.status;
            const msg = err?.response?.data?.message;
            if (status === 409 && msg) {
                addToast(msg, "info");
            } else {
                addToast("Errore durante l'eliminazione dell'incarico", "error");
            }
        }
    };

    const persistMovimenti = async (lista: any[]) => {
        if (!pratica) return;
        await PraticaService.patchDatiAggiuntivi(Number(id), { movimenti_contabili: lista });
        // Tiene allineato il JSON locale del mandato, così tornando su Contabilità i
        // movimenti restano visibili.
        handleDatiAggiuntiviChange({ movimenti_contabili: lista });
    };

    // Quando TabContabilita salva su datiAggiuntiviJson aggiorniamo la `pratica`
    // locale: il tab viene smontato al cambio e al rientro rilegge da qui.
    const handleDatiAggiuntiviChange = (patch: Record<string, any>) => {
        setPratica(prev => {
            if (!prev) return prev;
            let extra: any = {};
            const raw = (prev as any).datiAggiuntiviJson;
            try { extra = typeof raw === 'string' ? (JSON.parse(raw || '{}') || {}) : (raw || {}); } catch { extra = {}; }
            return { ...(prev as any), datiAggiuntiviJson: JSON.stringify({ ...extra, ...patch }) } as any;
        });
    };

    const handleAddPayment = async () => {
        if (!newPayment.importo || savingPayment) return;
        setSavingPayment(true);
        const newMov = { id: Date.now(), ...newPayment, data: newPayment.data || localIsoDate(), importo: Number(newPayment.importo) };
        const nuova = [...movimenti, newMov];
        setMovimenti(nuova);
        try {
            await persistMovimenti(nuova);
            addToast("Movimento registrato", "success");
        } catch (e) {
            setMovimenti(movimenti); // Rollback UI
            addToast("Errore nel salvataggio del movimento", "error");
            console.error('Errore salvataggio movimenti contabili:', e);
        } finally {
            setSavingPayment(false);
        }
        setShowPaymentModal(false);
        setNewPayment({ data: localIsoDate(), descrizione: '', importo: 0, tipo: 'entrata', ivaMode: DEFAULT_INCASSO_MODE });
    };

    const handleDeleteMovimento = async (movId: any) => {
        const nuova = movimenti.filter(m => m.id !== movId);
        setMovimenti(nuova);
        try {
            await persistMovimenti(nuova);
            addToast("Movimento eliminato", "success");
        } catch (e) {
            setMovimenti(movimenti); // Rollback UI
            addToast("Errore nell'eliminazione del movimento", "error");
            console.error('Errore eliminazione movimento contabile:', e);
        }
    };

    // Spunta "Fatturato" sul singolo movimento contabile: va nel JSON pratica
    // (movimenti_contabili[].fatturato) e il backend la usa in calcolaFatturato().
    const handleToggleFatturato = async (movId: any, fatturato: boolean) => {
        const nuova = movimenti.map(m =>
            m.id === movId ? { ...m, fatturato } : m
        );
        setMovimenti(nuova);
        try {
            await persistMovimenti(nuova);
        } catch (e) {
            setMovimenti(movimenti); // Rollback UI
            addToast("Errore nell'aggiornamento del movimento", "error");
            console.error('Errore toggle fatturato:', e);
        }
    };

    // Contatore del badge "Media (N)": deve corrispondere ai file visibili nelle
    // cartelle del tab Media. La Documentazione Ufficio non conta (isOffice=true).
    // Stessi filtri di TabMedia per i media degli incarichi:
    // - escluse le foto km (Km_Iniziali_* / Km_Finali_*)
    // - esclusi i media senza dataAcquisizione
    // - esclusi i media in attesa di approvazione (DUPLICATE / OUTSIDE_RANGE / PENDING_APPROVAL)
    // - esclusi quelli con originale non ancora disponibile su S3
    const mediaCount = useMemo(() => {
        let count = 0;
        incarichi.forEach(inc => {
            if (!inc.media) return;
            count += inc.media.filter((m: any) => {
                if (m.filename?.startsWith('Km_')) return false;
                if (!m.dataAcquisizione) return false;
                if (m.analysisStatus === 'DUPLICATE'
                    || m.analysisStatus === 'OUTSIDE_RANGE'
                    || m.analysisStatus === 'PENDING_APPROVAL') return false;
                const displayUrl = m.urlCompressed || m.finalUrl || m.urlOriginal;
                if (!displayUrl) return false;
                return true;
            }).length;
        });
        return count;
    }, [incarichi]);

    if (loading) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-blue-600" size={40}/></div>;
    if (forbidden) {
        return (
            <div className="p-8 text-center text-red-500">
                <AlertCircle size={40} className="mx-auto mb-2"/>
                <p>{messaggioPermessoNegato(user?.role)}</p>
                {user?.role === UserRole.RESPONSABILE && (
                    <button
                        onClick={() => navigate('/incarichi')}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg font-bold text-sm"
                    >
                        Vai a Miei Incarichi
                    </button>
                )}
            </div>
        );
    }
    if (error || !pratica) return <div className="p-8 text-center text-red-500"><AlertCircle size={40} className="mx-auto mb-2"/>{error}</div>;

    return (
        <div className="p-6 space-y-6 pb-20">
            {/* Header */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                <div className="space-y-2">
                    <button
                        onClick={() => navigate('/pratiche')}
                        className="flex items-center gap-2 text-gray-500 text-xs font-bold uppercase tracking-wider hover:text-blue-600 transition-colors"
                    >
                        <ChevronLeft size={14}/> Torna all'elenco
                    </button>

                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-extrabold text-gray-900">
                            {pratica.name || "Pratica Senza Nome"}
                        </h1>
                        <span className="text-xs font-mono bg-gray-100 text-gray-500 px-2 py-1 rounded">
                #{pratica.id}
            </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm">
                        <div className="flex items-center gap-1.5 text-gray-600 bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
                            <User size={14} className="text-gray-400"/>
                            {/*
                              Azienda: "Referente (Ragione Sociale)", perché il titolare interagisce con il
                              referente. Privato: solo il nome.
                            */}
                            <span>
                                Cliente:{' '}
                                <span className="font-bold text-gray-800">
                                    {(pratica as any).clientTipo === 'Azienda' && (pratica as any).clientReferente
                                        ? `${(pratica as any).clientReferente} (${pratica.clientName})`
                                        : pratica.clientName}
                                </span>
                                <ClientBadge deleted={(pratica as any).clienteDeleted} anonymized={(pratica as any).clienteAnonymized} />
                            </span>
                        </div>

                        {/*
                          Riferimento del mandato, sempre visibile a titolari e responsabili (anche
                          vuoto) per poterlo impostare da qui. Nascosto al collaboratore.
                        */}
                        {canEditRiferimento && (
                            editingRif ? (
                                <div className="inline-flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-md border border-amber-200">
                                    <Tag size={12} className="text-amber-500 shrink-0"/>
                                    <input
                                        autoFocus
                                        type="text"
                                        maxLength={120}
                                        value={editingRifValue}
                                        onChange={e => setEditingRifValue(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') { e.preventDefault(); saveEditRiferimentoHeader(); }
                                            else if (e.key === 'Escape') { e.preventDefault(); cancelEditRiferimentoHeader(); }
                                        }}
                                        disabled={savingRif}
                                        placeholder="Es. Milano / Sig. Rossi"
                                        className="w-44 px-2 py-0.5 text-xs border border-amber-300 rounded focus:ring-1 focus:ring-amber-500 outline-none disabled:bg-gray-100"
                                    />
                                    <button
                                        onClick={saveEditRiferimentoHeader}
                                        disabled={savingRif}
                                        className="p-0.5 text-emerald-600 hover:bg-emerald-100 rounded disabled:opacity-50"
                                        title="Salva"
                                    >
                                        {savingRif ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>}
                                    </button>
                                    <button
                                        onClick={cancelEditRiferimentoHeader}
                                        disabled={savingRif}
                                        className="p-0.5 text-slate-500 hover:bg-slate-100 rounded disabled:opacity-50"
                                        title="Annulla (Esc)"
                                    >
                                        <X size={12}/>
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={startEditRiferimentoHeader}
                                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-bold bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 transition-colors max-w-[260px]"
                                    title={(pratica as any).riferimento ? `Riferimento: ${(pratica as any).riferimento} — clicca per modificare` : 'Imposta riferimento'}
                                >
                                    <Tag size={12} className="shrink-0"/>
                                    <span className="truncate">
                                        {(pratica as any).riferimento || 'Imposta riferimento'}
                                    </span>
                                    <Edit2 size={10} className="shrink-0 opacity-60"/>
                                </button>
                            )
                        )}

                        {/* Badge stato pratica */}
                        <button
                            onClick={() => { setNewStato(pratica.stato || 'In Corso'); setShowStatusModal(true); }}
                            className={`px-3 py-1 rounded-full text-xs font-bold border transition-all flex items-center gap-2 shadow-sm hover:scale-105 ${getStatusColor(pratica.stato || '')}`}
                        >
                            <div className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                            {pratica.stato || 'In Corso'}
                            <ChevronDown size={12}/>
                        </button>

                        {/* BADGE STATO FIRMA OTP
                        {pratica.otpFirmaStatus === 'IN_ATTESA_FIRMA' && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold border bg-amber-50 text-amber-700 border-amber-200 flex items-center gap-1.5">
                                <Loader2 size={11} className="animate-spin"/> In Attesa di Firma
                            </span>
                        )}*/}
                        {pratica.otpFirmaStatus === 'MANDATO_FIRMATO' && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold border bg-blue-50 text-blue-700 border-blue-200 flex items-center gap-1.5">
                                <CheckSquare size={11}/> Mandato Firmato
                            </span>
                        )}
                        {/* Il BE (FirmaCallbackService) scrive SOGGETTO_FIRMATO; PRIVACY_FIRMATA è il nome precedente. */}
                        {(pratica.otpFirmaStatus === 'SOGGETTO_FIRMATO' || pratica.otpFirmaStatus === 'PRIVACY_FIRMATA') && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold border bg-blue-50 text-blue-700 border-blue-200 flex items-center gap-1.5">
                                <CheckSquare size={11}/> Dati Soggetto Firmati
                            </span>
                        )}
                        {pratica.otpFirmaStatus === 'FIRMATA' && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold border bg-emerald-50 text-emerald-700 border-emerald-200 flex items-center gap-1.5">
                                <CheckSquare size={11}/> ✓ Documenti Firmati
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 lg:justify-end w-full lg:w-auto">

                    {/* Gruppo 1: storico e modifica */}
                    <div className="flex bg-gray-100 p-1 rounded-xl mr-2">
                        <button
                            onClick={handleOpenHistory}
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-white rounded-lg transition-all"
                            title="Cronologia Versioni"
                        >
                            <History size={20} />
                        </button>
                        <button
                            onClick={() => navigate(`/pratiche/modifica/${id}`)}
                            className="flex items-center gap-2 p-2 text-amber-600 hover:bg-white rounded-lg transition-all font-bold text-sm"
                            title="Modifica Pratica"
                        >
                            <Edit2 size={18}/> <span className="hidden xl:inline">Modifica</span>
                        </button>
                    </div>

                    {/* Gruppo 2: documenti */}
                    {/*
                      Menu "Scarica":
                      - Mandato: sempre
                      - Soggetto investigato: solo per l'investigativo
                      - Informativa Privacy: sempre (documento aziendale)
                    */}
                    <div className="flex items-center gap-2 border-l border-r border-gray-200 px-3">
                        <div className="relative">
                            <button
                                onClick={() => setShowDownloadMenu(v => !v)}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                                title="Scarica documento"
                            >
                                <Download size={18}/> <span className="hidden sm:inline">Scarica</span>
                                <ChevronDown size={14}/>
                            </button>
                            {showDownloadMenu && (
                                <>
                                    <div
                                        className="fixed inset-0 z-10"
                                        onClick={() => setShowDownloadMenu(false)}
                                    />
                                    <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                                        <button
                                            onClick={() => handleDownloadDoc('mandato')}
                                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors"
                                        >
                                            <FileText size={16}/> Mandato
                                        </button>
                                        {pratica.modelId === 'mand-investigativo' && (
                                            <button
                                                onClick={() => handleDownloadDoc('soggetto')}
                                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors border-t border-gray-100"
                                            >
                                                <User size={16}/> Soggetto investigato
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleDownloadDoc('privacy')}
                                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors border-t border-gray-100"
                                        >
                                            <ShieldCheck size={16}/> Informativa Privacy
                                        </button>
                                        {relazioniPdf.length === 0 ? (
                                            <div
                                                className="w-full px-4 py-2.5 text-sm text-gray-400 flex items-center gap-2 border-t border-gray-100 cursor-default"
                                                title="Generala dall'editor DOCX della relazione con il pulsante Scarica PDF"
                                            >
                                                <FileText size={16}/> Relazione <span className="text-[11px]">(non ancora generata)</span>
                                            </div>
                                        ) : relazioniPdf.map((f: any) => (
                                            <button
                                                key={f.id}
                                                onClick={() => handleDownloadRelazione(f)}
                                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors border-t border-gray-100"
                                                title="Ultima versione scaricata dall'editor DOCX"
                                            >
                                                <FileText size={16} className="shrink-0"/> <span className="truncate">{labelRelazione(f)}</span>
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                        <button
                            onClick={() => {
                                // Si lavora solo con l'editor DOCX: con più soggetti si sceglie prima quale,
                                // altrimenti si apre subito. La scelta dell'editor (step 1 qui sotto) non è
                                // più raggiungibile.
                                if (soggettiList.length > 1) { setChooserSoggettoFor('docx'); setShowEditorChooser(true); }
                                else navigate(`/relazione-docx/${id}`);
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-2 text-sm font-bold transition-colors"
                        >
                            <ClipboardCheck size={18}/> <span className="hidden sm:inline">Relazione Finale</span>
                        </button>
                        {showEditorChooser && (
                            <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" onClick={() => { setShowEditorChooser(false); setChooserSoggettoFor(null); }}>
                                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
                                    {chooserSoggettoFor !== null ? (
                                        // Step 2, solo con più soggetti: scelta del soggetto. Ogni soggetto ha la sua
                                        // relazione, sia web sia DOCX.
                                        <>
                                            <h3 className="text-lg font-bold text-gray-800 mb-1">Quale soggetto?</h3>
                                            <p className="text-sm text-gray-500 mb-4">Questa pratica ha più soggetti: ognuno ha la sua relazione.</p>
                                            <div className="space-y-2 max-h-72 overflow-auto">
                                                {soggettiList.map((s: any, i: number) => (
                                                    <button
                                                        key={i}
                                                        onClick={() => {
                                                            setShowEditorChooser(false);
                                                            const q = `?soggetto=${encodeURIComponent(s?.nome || '')}`;
                                                            const dest = chooserSoggettoFor === 'docx' ? `/relazione-docx/${id}${q}` : `/report-editor/${id}${q}`;
                                                            setChooserSoggettoFor(null);
                                                            navigate(dest);
                                                        }}
                                                        className="w-full text-left p-3 rounded-xl border-2 border-indigo-200 bg-indigo-50 hover:bg-indigo-100 transition-colors flex items-center gap-3"
                                                    >
                                                        <User size={18} className="text-indigo-600 shrink-0"/>
                                                        <span className="font-semibold text-gray-800">{s?.nome || `Soggetto ${i + 1}`}</span>
                                                    </button>
                                                ))}
                                            </div>
                                            <button onClick={() => { setShowEditorChooser(false); setChooserSoggettoFor(null); }} className="mt-4 w-full text-sm text-gray-500 hover:text-gray-700 py-2">Annulla</button>
                                        </>
                                    ) : (
                                        // Step 1: scelta dell'editor.
                                        <>
                                            <h3 className="text-lg font-bold text-gray-800 mb-1">Apri relazione</h3>
                                            <p className="text-sm text-gray-500 mb-4">Scegli con quale editor lavorare.</p>
                                            <div className="space-y-3">
                                                <button
                                                    onClick={() => {
                                                        const modelId = (pratica as any)?.modelId;
                                                        // Bonifica e consulenza hanno un solo soggetto: si apre subito l'editor.
                                                        if (modelId === 'prev-bonifiche')      { setShowEditorChooser(false); navigate(`/report-editor-bonifiche/${id}`); }
                                                        else if (modelId === 'prev-consulenza') { setShowEditorChooser(false); navigate(`/report-editor-consulenza/${id}`); }
                                                        // Investigativo con più soggetti: si chiede il soggetto.
                                                        else if (soggettiList.length > 1)       { setChooserSoggettoFor('html'); }
                                                        else                                    { setShowEditorChooser(false); navigate(`/report-editor/${id}`); }
                                                    }}
                                                    className="w-full text-left p-4 rounded-xl border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors flex items-center gap-3"
                                                >
                                                    <ClipboardCheck size={22} className="text-blue-600 shrink-0"/>
                                                    <span>
                                                        <span className="block font-bold text-gray-800">Editor web</span>
                                                        <span className="block text-xs text-gray-500">Editor HTML (template, foto, annotazioni).{soggettiList.length > 1 ? ' Una relazione per soggetto.' : ''}</span>
                                                    </span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        // Con più soggetti si chiede quale; altrimenti si apre la relazione unica.
                                                        if (soggettiList.length > 1) { setChooserSoggettoFor('docx'); }
                                                        else { setShowEditorChooser(false); navigate(`/relazione-docx/${id}`); }
                                                    }}
                                                    className="w-full text-left p-4 rounded-xl border-2 border-indigo-200 bg-indigo-50 hover:bg-indigo-100 transition-colors flex items-center gap-3"
                                                >
                                                    <FileText size={22} className="text-indigo-600 shrink-0"/>
                                                    <span>
                                                        <span className="block font-bold text-gray-800">DOCX</span>
                                                        <span className="block text-xs text-gray-500">Editor Word-like, impaginazione nativa.{soggettiList.length > 1 ? ' Una relazione per soggetto.' : ''}</span>
                                                    </span>
                                                </button>
                                            </div>
                                            <button onClick={() => setShowEditorChooser(false)} className="mt-4 w-full text-sm text-gray-500 hover:text-gray-700 py-2">Annulla</button>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Gruppo 3: azioni principali */}
                    <div className="ml-2 flex items-center gap-2">
                        {(pratica.stato === 'Da Attivare' || !pratica.stato || pratica.stato === 'BOZZA') && (
                            <button
                                onClick={handleAttivaLavoro}
                                className="bg-green-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-green-700 flex items-center gap-2 shadow-lg shadow-green-200 transition-all active:scale-95"
                            >
                                <PlayCircle size={20}/> Avvia Lavoro
                            </button>
                        )}

                        {/* Firma digitale, sempre visibile e adattata allo stato */}
                        {!pratica.otpFirmaStatus && (
                            <button
                                onClick={handleApriModalFirma}
                                className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-indigo-700 flex items-center gap-2 shadow-lg shadow-indigo-200 transition-all active:scale-95"
                            >
                                <Smartphone size={20}/> Firma Digitale
                            </button>
                        )}

                        {(pratica.otpFirmaStatus === 'IN_ATTESA_FIRMA' ||
                          pratica.otpFirmaStatus === 'MANDATO_FIRMATO' ||
                          pratica.otpFirmaStatus === 'SOGGETTO_FIRMATO' ||
                          pratica.otpFirmaStatus === 'PRIVACY_FIRMATA') && (
                            <button
                                disabled
                                className="bg-amber-100 text-amber-600 border border-amber-200 px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 cursor-not-allowed opacity-80"
                                title="In attesa che il cliente firmi i documenti"
                            >
                                <Loader2 size={18} className="animate-spin"/> In Attesa di Firma
                            </button>
                        )}

                        {pratica.otpFirmaStatus === 'FIRMATA' && (
                            <>
                                <span className="bg-emerald-50 border border-emerald-300 text-emerald-700 px-4 py-2.5 rounded-xl font-bold flex items-center gap-2 text-sm">
                                    <CheckSquare size={17}/> ✓ Firmato
                                </span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Tab e contenuto */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[600px] flex flex-col">
                <div className="flex border-b border-gray-200 bg-gray-50 px-2 overflow-x-auto scrollbar-hide">
                    {[
                        { id: 'riepilogo', label: 'Riepilogo', icon: <LayoutDashboard size={16}/> },
                        { id: 'incarichi', label: 'Incarichi', icon: <CheckSquare size={16}/> },
                        { id: 'report', label: 'Report dal Campo', icon: <Smartphone size={16}/> },
                        { id: 'media', label: `Media (${mediaCount})`, icon: <ImageIcon size={16}/> },
                        { id: 'contabilita', label: 'Contabilità', icon: <DollarSign size={16}/> }
                    ].map(tab => (
                        <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                            {tab.icon} {tab.label}
                        </button>
                    ))}
                </div>

                <div className="p-6 flex-1 overflow-y-auto bg-white">
                    {activeTab === 'riepilogo' && (
                        <TabRiepilogo
                            pratica={pratica}
                            teamStats={teamStats}
                            fileHistory={pratica.files?.filter(f => f.name.toLowerCase().includes("mandato"))}
                        />
                    )}
                    {activeTab === 'riepilogo' && canEditRiferimento && (STATI_SVUOTA_SOGGETTO.includes(pratica.stato || '') || pratica.soggettoAnonimizzatoAt) && (
                        <div className="mt-8 border border-rose-200 rounded-xl p-4 bg-rose-50/40 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                            <div className="text-sm">
                                <p className="font-bold text-rose-800 flex items-center gap-2"><UserX size={16}/> Dati del soggetto investigato</p>
                                {pratica.soggettoAnonimizzatoAt ? (
                                    <p className="text-xs text-rose-700 mt-1">
                                        Svuotati il {new Date(pratica.soggettoAnonimizzatoAt).toLocaleDateString('it-IT')}: del soggetto restano solo le iniziali.
                                    </p>
                                ) : (
                                    <p className="text-xs text-gray-600 mt-1">
                                        Il mandato non è più operativo: puoi ridurre il soggetto alle sole iniziali e cancellarne i dati. L'operazione è irreversibile.
                                    </p>
                                )}
                            </div>
                            {!pratica.soggettoAnonimizzatoAt && (
                                <button
                                    onClick={() => { setSvuotaRelazione(false); setSvuotaPdfSoggetto(false); setSvuotaConferma(''); setShowSvuotaSoggetto(true); }}
                                    className="shrink-0 px-4 py-2 rounded-lg text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 shadow-sm"
                                >
                                    Svuota dati soggetto investigato
                                </button>
                            )}
                        </div>
                    )}
                    {activeTab === 'incarichi' && (
                        <TabIncarichi
                            incarichi={incarichiOperativi}
                            onOpenAssign={() => setShowAssignModal(true)}
                            onOpenAPosteriori={() => setShowAPosteriori(true)}
                            onRevoca={handleRevocaIncarico}
                            onElimina={handleDeleteIncarico}
                            onNoteUpdated={async () => {
                                const updated = await IncaricoService.getByPratica(Number(id), { includeAutogenerati: true });
                                setIncarichi(Array.isArray(updated) ? updated : (updated as any).data || []);
                                addToast('Note aggiornate', 'success');
                            }}
                            soggettiList={soggettiList}
                        />
                    )}
                    {activeTab === 'report' && <TabReport praticaId={Number(id)} onApproved={refreshReports} soggetti={soggettiList} riferimento={canEditRiferimento ? (pratica as any)?.riferimento : null} />}
                    {activeTab === 'media' && <TabMedia pratica={pratica} incarichi={incarichi} onRefresh={refreshIncarichi} />}
                    {activeTab === 'contabilita' && (
                        <TabContabilita
                            praticaId={Number(id)}
                            pratica={pratica}
                            settings={settings}
                            movimenti={movimenti}
                            onOpenPayment={() => setShowPaymentModal(true)}
                            onDeleteMovimento={handleDeleteMovimento}
                            onToggleFatturato={handleToggleFatturato}
                            onDatiAggiuntiviChange={handleDatiAggiuntiviChange}
                        />
                    )}
                </div>
            </div>

            {/* Modale cambio stato */}
            {showStatusModal && (
                <Modal title="Modifica Stato Pratica" onClose={() => setShowStatusModal(false)}>
                    <div className="space-y-5">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Nuovo Stato</label>
                            <select
                                className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                                value={newStato}
                                onChange={(e) => setNewStato(e.target.value)}
                            >
                                <option value="BOZZA">BOZZA</option>
                                <option value="Da Attivare">Da Attivare (Iniziale)</option>
                                <option value="In Corso">In Corso (Operativa)</option>
                                <option value="Sospesa">Sospesa (Pausa)</option>
                                <option value="Conclusa">Conclusa / Completata</option>
                                <option value="Archiviata">Archiviata (fuori dai KPI)</option>
                                <option value="Annullata">Annullata</option>
                            </select>
            {/*
              Questi stati tolgono il mandato dai widget ma il credito resta: lo diciamo
              qui, dove si sceglie, e non solo in Contabilità.
            */}
                            {(newStato === 'Archiviata' || newStato === 'Sospesa' || newStato === 'Annullata') && (
                                <p className="mt-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-2.5">
                                    {newStato === 'Archiviata' && <>L'archiviazione rimuove la pratica dai widget <strong>Da Riscuotere</strong> e <strong>Da Fatturare</strong>. Usala quando il ciclo gestionale e fiscale del mandato è chiuso definitivamente.</>}
                                    {newStato === 'Sospesa' && <>La sospensione rimuove la pratica dai widget <strong>Da Riscuotere</strong> e <strong>Da Fatturare</strong> finché il mandato non viene riattivato.</>}
                                    {newStato === 'Annullata' && <>L'annullamento rimuove la pratica dai widget <strong>Da Riscuotere</strong> e <strong>Da Fatturare</strong>.</>}
                                    {' '}Se resta un importo non incassato o una fattura mai emessa, continuerà a comparire in
                                    {' '}<strong>Contabilità</strong> fra i mandati con importi non riscossi o non fatturati.
                                </p>
                            )}
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <button onClick={() => setShowStatusModal(false)} className="px-5 py-2.5 text-gray-600 font-semibold hover:bg-gray-100 rounded-xl transition-colors">Annulla</button>
                            <button onClick={handleCambiaStato} className="bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-blue-700 shadow-md transition-all active:scale-[0.98]">
                                Conferma Modifica
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Avviso incarichi ancora aperti (alla conclusione) */}
            {avvisoIncarichi && (
                <Modal title="Incarichi ancora aperti" onClose={() => setAvvisoIncarichi(null)}>
                    <div className="space-y-4" data-testid="avviso-incarichi-aperti">
                        <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                            <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
                            <div className="text-sm text-amber-900 space-y-1.5">
                                <p className="font-bold">{fraseIncarichiAperti(avvisoIncarichi.conteggio)}</p>
                                <p>{ISTRUZIONI_INCARICHI_APERTI}</p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <button onClick={() => setAvvisoIncarichi(null)} className="px-5 py-2.5 text-gray-600 font-semibold hover:bg-gray-100 rounded-xl transition-colors">Annulla</button>
                            <button
                                onClick={() => {
                                    const r = avvisoIncarichi.riepilogo;
                                    setAvvisoIncarichi(null);
                                    proseguiConclusione(r);
                                }}
                                className="bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-blue-700 shadow-md transition-all active:scale-[0.98]"
                            >
                                Conferma
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modale registrazione fattura (alla conclusione) */}
            {showFatturaModal && (() => {
                // Valori dal BE (getRiepilogoChiusura), coerenti col tab Contabilità sia a corpo
                // sia a consuntivo. Se la chiamata fallisce: preventivo × 1.22.
                const baseIvato = chiusuraRiepilogo?.baseIvato
                    ?? Math.round(budgetDati.preventivo * 1.22 * 100) / 100;
                const totaleIncassato = chiusuraRiepilogo?.totaleIncassato ?? 0;
                const saldoResiduo = chiusuraRiepilogo?.saldoResiduo
                    ?? Math.max(0, baseIvato - totaleIncassato);
                const isConsuntivo = chiusuraRiepilogo?.tipoCompenso === 'consuntivo';
                const primoBoxLabel = isConsuntivo ? 'Consuntivo Maturato (IVA incl.)' : 'Concordato (IVA incl.)';
                const fmt = (v: number) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v);
                return (
                    <Modal title="Mandato Concluso — Registrazione Fattura" onClose={() => setShowFatturaModal(false)}>
                        <div className="space-y-4">
                            {/* Riepilogo finanziario */}
                            {baseIvato > 0 && (
                                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 grid grid-cols-3 gap-3 text-center">
                                    <div>
                                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">{primoBoxLabel}</p>
                                        <p className="text-base font-black text-gray-800">{fmt(baseIvato)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Già Incassato</p>
                                        <p className="text-base font-black text-emerald-600">{fmt(totaleIncassato)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Da Riscuotere</p>
                                        <p className={`text-base font-black ${saldoResiduo > 0 ? 'text-orange-600' : 'text-gray-400'}`}>{fmt(saldoResiduo)}</p>
                                    </div>
                                </div>
                            )}

                            <p className="text-sm font-semibold text-gray-700">Hai emesso una fattura per questo mandato?</p>

                            <div className="space-y-2">
                                {/* Fattura sul residuo da riscuotere (gli incassi precedenti sono già movimenti) */}
                                <button
                                    onClick={() => handleConfermaConclusioneConOpzione('totale')}
                                    disabled={concludendo}
                                    className="disabled:opacity-60 disabled:cursor-not-allowed w-full text-left p-4 border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                                >
                                    <p className="font-bold text-blue-800 text-sm">Sì, registro la fattura e creo movimento</p>
                                    <p className="text-xs text-blue-700 mt-0.5">
                                        {fmt(saldoResiduo)} — importo ancora da riscuotere (IVA incl.)
                                    </p>
                                </button>

                                {/* Nessuna fattura */}
                                <button
                                    onClick={() => handleConfermaConclusioneConOpzione('nessuna')}
                                    disabled={concludendo}
                                    className="disabled:opacity-60 disabled:cursor-not-allowed w-full text-left p-4 border-2 border-gray-200 bg-white hover:bg-gray-50 rounded-xl transition-colors"
                                >
                                    <p className="font-bold text-gray-700 text-sm">Nessuna fattura per ora</p>
                                    <p className="text-xs text-gray-500 mt-0.5">Il mandato viene concluso. Potrai registrare la fattura in seguito dall'amministrazione.</p>
                                </button>
                            </div>
                        </div>
                    </Modal>
                );
            })()}

            {/* Modale assegna incarico */}
            {showAPosteriori && (
                <LavoroAPosterioriModal
                    mode="incarico"
                    praticaId={Number(id)}
                    collaboratori={collaboratori.map(c => ({ id: c.id as number, nome: `${c.name} ${c.surname || ''}`.trim() }))}
                    soggetti={['prev-bonifiche', 'prev-consulenza'].includes((pratica as any)?.modelId)
                        ? [] : soggettiList.map((s: any) => s.nome).filter(Boolean)}
                    onClose={() => setShowAPosteriori(false)}
                    onSaved={() => { refreshIncarichi(); refreshReports(); }}
                />
            )}
            {showAssignModal && (
                <Modal title="Assegna Incarico" onClose={() => setShowAssignModal(false)}>
                    <div className="space-y-4">

                        {/* Collaboratore */}
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-sm font-medium">Collaboratore</label>
                                <button
                                    type="button"
                                    onClick={() => setShowAgendaSettimanale(true)}
                                    className="flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 px-2 py-1 rounded-md transition-colors"
                                    title="Apri agenda settimanale per scegliere chi è libero negli orari che ti servono"
                                >
                                    <CalendarDays size={14}/> Vedi disponibilità
                                </button>
                            </div>
                            <select className="w-full p-2.5 border rounded-lg bg-white" value={assignForm.collaboratoreId} onChange={e => setAssignForm({...assignForm, collaboratoreId: e.target.value})}>
                                <option value="">-- Seleziona --</option>
                                {collaboratori.map(c => <option key={c.id} value={c.id}>{c.name} {c.surname}</option>)}
                            </select>
                        </div>

                        {/* Soggetto da investigare */}
                        {soggettiList.length > 0 && (
                            <div>
                                <label className="block text-sm font-medium mb-1 text-orange-700">Soggetto Investigato</label>
                                {soggettiList.length === 1 ? (
                                    <div className="w-full p-2.5 border border-orange-200 rounded-lg bg-orange-50 text-gray-800 font-medium">
                                        {soggettiList[0].nome}
                                    </div>
                                ) : (
                                    <>
                                        <select
                                            className={`w-full p-2.5 border rounded-lg bg-orange-50 outline-none focus:ring-2 focus:ring-orange-400 ${assignMissingSoggetto ? 'border-red-400 ring-1 ring-red-300' : 'border-orange-200'}`}
                                            value={assignForm.soggettoNome}
                                            onChange={e => setAssignForm({...assignForm, soggettoNome: e.target.value})}
                                        >
                                            <option value="">-- Seleziona soggetto --</option>
                                            {soggettiList.map((sogg: any, idx: number) => (
                                                <option key={idx} value={sogg.nome}>{sogg.nome}</option>
                                            ))}
                                        </select>
                                        {assignMissingSoggetto && (
                                            <p className="text-xs text-red-600 mt-1">Seleziona il soggetto investigato per assegnare l'incarico.</p>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Inizio *</label>
                                <input type="datetime-local" className="w-full p-2.5 border rounded-lg" value={assignForm.dataInizio} onChange={e => setAssignForm({...assignForm, dataInizio: e.target.value, orariPreset: false})}/>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Fine *</label>
                                <input
                                    type="datetime-local"
                                    className={`w-full p-2.5 border rounded-lg ${assignDataFineInvalida ? 'border-red-400 ring-1 ring-red-300' : ''}`}
                                    value={assignForm.dataFine}
                                    min={assignForm.dataInizio || undefined}
                                    onChange={e => setAssignForm({...assignForm, dataFine: e.target.value, orariPreset: false})}
                                />
                                {assignDataFineInvalida && (
                                    <p className="text-xs text-red-600 mt-1">La data di fine deve essere uguale o successiva alla data di inizio.</p>
                                )}
                            </div>
                        </div>

                        {/*
                          Durate rapide: se dataInizio è vuota la imposta a ora, poi porta dataFine
                          alle 23:59 del giorno corrispondente. Il backend divide il periodo in
                          incarichi giornalieri.
                        */}
                        <div className="flex flex-wrap gap-2 -mt-2">
                            {DATA_FINE_PRESETS.map(p => (
                                <button
                                    key={p.label}
                                    type="button"
                                    onClick={() => {
                                        const dataInizio = assignForm.dataInizio || nowDatetimeLocal();
                                        setAssignForm({
                                            ...assignForm,
                                            dataInizio,
                                            dataFine: computeDataFinePreset(dataInizio, p.daysOffset),
                                            orariPreset: true,
                                        });
                                    }}
                                    className="px-3 py-1.5 text-xs font-bold rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors"
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        {/*
                          Solo se la fine cade il giorno dopo l'inizio: un unico incarico a cavallo
                          della mezzanotte invece di due tranche.
                        */}
                        {assignContinuativoEligible && (
                            <label className="flex items-start gap-2.5 p-3 rounded-lg border border-indigo-200 bg-indigo-50 cursor-pointer">
                                <input type="checkbox" className="mt-0.5" checked={!!assignForm.continuativo}
                                       onChange={e => setAssignForm({...assignForm, continuativo: e.target.checked})}/>
                                <span className="text-xs text-indigo-900">
                                    <span className="font-bold">Continuativo</span> — un solo incarico a cavallo della mezzanotte:
                                    un solo report (datato al giorno di inizio), Start e Stop possibili in entrambi i giorni,
                                    ore cliente conteggiate oltre la mezzanotte. Senza spunta vengono creati due incarichi giornalieri.
                                </span>
                            </label>
                        )}

                        {/*<div><label className="block text-sm font-medium mb-1">Luogo / Punto d'incontro</label><input type="text" className="w-full p-2.5 border rounded-lg" value={assignForm.luogo} onChange={e => setAssignForm({...assignForm, luogo: e.target.value})}/></div>*/}

                        {/*
                          Scadenza di accettazione opzionale: allo scadere il backend rifiuta
                          l'incarico se il collaboratore non ha accettato.
                        */}
                        <div className="bg-amber-50/60 border border-amber-200 rounded-lg p-3">
                            <label className="block text-xs font-bold text-amber-800 uppercase tracking-wider mb-1.5">
                                Scadenza accettazione <span className="font-normal normal-case text-amber-700/70">(opzionale)</span>
                            </label>
                            <input
                                type="datetime-local"
                                className="w-full p-2.5 border border-amber-200 rounded-lg bg-white focus:ring-2 focus:ring-amber-300 outline-none"
                                value={assignForm.dataScadenzaAccettazione}
                                onChange={e => setAssignForm({ ...assignForm, dataScadenzaAccettazione: e.target.value })}
                                min={assignForm.dataInizio || undefined}
                            />
                            <p className="text-[11px] text-amber-700/80 mt-1.5 leading-snug">
                                Se compilata, l'incarico verrà <strong>rifiutato automaticamente</strong> qualora il collaboratore non abbia cliccato "Accetta" entro questa data/ora.
                            </p>
                        </div>

                        <div><label className="block text-sm font-medium mb-1">Note</label><textarea className="w-full p-2.5 border rounded-lg" rows={3} value={assignForm.note} onChange={e => setAssignForm({...assignForm, note: e.target.value})}/></div>

                        <button
                            className="w-full bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 mt-2 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:hover:bg-gray-300"
                            onClick={handleAssignSubmit}
                            disabled={!assignCanConfirm || assigning}
                        >{assigning ? 'Assegnazione…' : 'Conferma'}</button>
                    </div>
                </Modal>
            )}

            {showAgendaSettimanale && (
                <AgendaSettimanaleModal
                    users={collaboratori}
                    initialDate={assignForm.dataInizio || undefined}
                    onClose={() => setShowAgendaSettimanale(false)}
                    onSelectCollaboratore={(userId) => setAssignForm({ ...assignForm, collaboratoreId: String(userId) })}
                />
            )}

            {/* Modale pagamenti */}
            {showPaymentModal && (
                <Modal title="Registra Incasso da Cliente" onClose={() => setShowPaymentModal(false)}>
                    <div className="space-y-4">
                        <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 text-xs text-emerald-700 font-bold uppercase tracking-wider flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500"/> Incasso da Cliente (+)
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 mb-1" htmlFor="incasso-data">Data incasso</label>
                            <input id="incasso-data" type="date" className="w-full p-2 border rounded" value={newPayment.data}
                                   onChange={e => setNewPayment({...newPayment, data: e.target.value})}/>
                        </div>
                        <NumberInput
                               min={0} className="w-full p-2 border rounded" placeholder="Importo (€)" value={newPayment.importo || ''} onChange={e => setNewPayment({...newPayment, importo: Number(e.target.value), tipo: 'entrata'})}/>
                        {/* Modalità IVA dell'incasso (3 stati). */}
                        <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                                {(['LORDO', 'NETTO', 'SOLO_IVA'] as IvaMode[]).map(m => (
                                    <button key={m} type="button" onClick={() => setNewPayment({...newPayment, ivaMode: m})}
                                        className={`text-xs font-bold px-2.5 py-1 rounded-lg border transition-colors ${
                                            newPayment.ivaMode === m ? 'bg-blue-600 text-white border-blue-600'
                                                : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'}`}>
                                        {IVA_MODE_LABEL[m]}
                                    </button>
                                ))}
                            </div>
                            {newPayment.importo > 0 && (() => {
                                const s = splitIncasso(Number(newPayment.importo), newPayment.ivaMode);
                                return <div className="text-[11px] text-slate-500 mt-1">Imponibile € {s.imp.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})} · IVA € {s.iva.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>;
                            })()}
                        </div>
                        <input className="w-full p-2 border rounded" placeholder="Descrizione (Es. Bonifico saldo)" value={newPayment.descrizione} onChange={e => setNewPayment({...newPayment, descrizione: e.target.value})}/>
                        <button className="w-full bg-green-600 text-white py-2 rounded disabled:opacity-60 disabled:cursor-not-allowed" onClick={handleAddPayment} disabled={savingPayment}>{savingPayment ? 'Salvataggio…' : 'Salva Registrazione'}</button>
                    </div>
                </Modal>
            )}

            {/* Modale dati mancanti */}
            {showDatiMancantiModal && (
                <Modal title="Dati Cliente Mancanti" onClose={() => setShowDatiMancantiModal(false)}>
                    <div className="space-y-4">
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
                            <AlertCircle size={20} className="text-amber-600 shrink-0 mt-0.5"/>
                            <div>
                                <p className="font-bold text-amber-800 text-sm">Email e/o telefono del cliente mancanti</p>
                                <p className="text-amber-700 text-xs mt-1">
                                    Per avviare la firma digitale tramite OTP Service, il cliente deve avere
                                    sia l'<strong>email</strong> che il <strong>numero di telefono</strong> registrati.
                                    OTP Service li usa per inviare il link di firma e il codice OTP.
                                </p>
                            </div>
                        </div>
                        <p className="text-sm text-gray-600">
                            Aggiorna i dati del cliente prima di procedere con la firma digitale.
                        </p>
                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                onClick={() => setShowDatiMancantiModal(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={() => {
                                    setShowDatiMancantiModal(false);
                                    navigate(`/clienti/${(pratica as any)?.clientId}`);
                                }}
                                className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-indigo-700 flex items-center gap-2"
                            >
                                <User size={16}/> Vai alla scheda cliente
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modale avvio firma OTP */}
            {showFirmaModalOTP && (() => {
                // Solo l'investigativo ha "Dati Soggetto" come secondo documento da firmare.
                const isInvestigativo = pratica?.modelId === 'mand-investigativo';
                return (
                <Modal title="Avvia Firma Elettronica" onClose={() => setShowFirmaModalOTP(false)}>
                    <div className="space-y-4">
                        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex gap-3">
                            <Smartphone size={20} className="text-indigo-600 shrink-0 mt-0.5"/>
                            <div>
                                <p className="font-bold text-indigo-800 text-sm">Firma con OTP Service</p>
                                <p className="text-indigo-700 text-xs mt-1">
                                    {isInvestigativo
                                        ? <>Verranno inviati <strong>2 documenti</strong> a </>
                                        : <>Verrà inviato <strong>il documento</strong> a </>}
                                    <strong>{pratica?.clientName}</strong> per la firma elettronica avanzata:
                                </p>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                                <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-black">1</div>
                                <div>
                                    <p className="text-sm font-bold text-gray-800">Mandato di Incarico</p>
                                    <p className="text-xs text-gray-400">Il PDF del mandato compilato per questa pratica</p>
                                </div>
                            </div>
                            {isInvestigativo && (
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                                    <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-black">2</div>
                                    <div>
                                        <p className="text-sm font-bold text-gray-800">Dati Soggetto Investigato</p>
                                        <p className="text-xs text-gray-400">Il PDF con i dati del soggetto da investigare, allegato alla pratica</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <p className="text-xs text-gray-400 bg-gray-50 rounded-lg p-3 border border-gray-100">
                            Il cliente riceverà una email con il link per firmare.
                            Una volta firmati entrambi i documenti, i PDF firmati verranno salvati automaticamente nella pratica.
                        </p>

                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                onClick={() => setShowFirmaModalOTP(false)}
                                disabled={isFirmaLoading}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium disabled:opacity-50"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={() => { setShowFirmaModalOTP(false); handleAvviaFirmaOTP(true); }}
                                disabled={isFirmaLoading}
                                className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-indigo-700 flex items-center gap-2 shadow-lg shadow-indigo-200 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {isFirmaLoading
                                    ? <><Loader2 className="animate-spin" size={18}/> Invio in corso...</>
                                    : <><Smartphone size={18}/> Conferma e Invia</>
                                }
                            </button>
                        </div>
                    </div>
                </Modal>
                );
            })()}

            {showSvuotaSoggetto && pratica && (
                <Modal title="Svuota dati soggetto investigato" onClose={() => !isSvuotando && setShowSvuotaSoggetto(false)}>
                    <div className="space-y-4 text-sm">
                        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-rose-800">
                            <p className="font-bold mb-1">Operazione irreversibile.</p>
                            <ul className="list-disc pl-5 space-y-0.5 text-xs">
                                <li>Il nome del soggetto diventa le sole iniziali (es. "Mario Rossi" → "M. R."), nel mandato e negli incarichi.</li>
                                <li>Si cancellano gli altri dati del soggetto (indirizzo, auto, abitudini, telefono…).</li>
                                <li>Si cancellano luogo e note degli incarichi e il testo delle relazioni giornaliere.</li>
                            </ul>
                        </div>
                        <p className="text-xs text-gray-600">
                            Restano: cliente, importi, fatture, incarichi con ore, km e spese, PDF del mandato firmato, foto e video
                            (per quelli usa "Svuota" nel File Manager).
                        </p>
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input type="checkbox" className="mt-0.5" checked={svuotaRelazione} onChange={e => setSvuotaRelazione(e.target.checked)} disabled={isSvuotando}/>
                            <span><strong>Elimina anche la relazione finale</strong> <span className="text-gray-500">(testo, file Word e PDF archiviati). Se non la elimini resta, intestata alle iniziali.</span></span>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input type="checkbox" className="mt-0.5" checked={svuotaPdfSoggetto} onChange={e => setSvuotaPdfSoggetto(e.target.checked)} disabled={isSvuotando}/>
                            <span><strong>Elimina anche il PDF "Dati soggetto investigato"</strong> <span className="text-gray-500">(anche la copia firmata).</span></span>
                        </label>
                        <div>
                            <label className="text-xs font-bold text-gray-600">Per confermare scrivi <span className="font-mono text-rose-700">SVUOTA</span></label>
                            <input
                                value={svuotaConferma}
                                onChange={e => setSvuotaConferma(e.target.value)}
                                disabled={isSvuotando}
                                className="w-full mt-1 p-2 border border-gray-300 rounded-lg font-mono"
                            />
                        </div>
                        <div className="flex justify-end gap-3 pt-1">
                            <button onClick={() => setShowSvuotaSoggetto(false)} disabled={isSvuotando} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium disabled:opacity-50">
                                Annulla
                            </button>
                            <button
                                disabled={isSvuotando || svuotaConferma.trim().toUpperCase() !== 'SVUOTA'}
                                onClick={async () => {
                                    setIsSvuotando(true);
                                    try {
                                        await PraticaService.svuotaDatiSoggetto(Number(pratica.id), {
                                            eliminaRelazione: svuotaRelazione, eliminaPdfSoggetto: svuotaPdfSoggetto,
                                        });
                                        const refreshed: any = await PraticaService.getById(Number(pratica.id));
                                        setPratica(refreshed?.data || refreshed);
                                        setShowSvuotaSoggetto(false);
                                        addToast('Dati del soggetto svuotati', 'success');
                                    } catch (e: any) {
                                        addToast(e?.response?.data?.message || 'Impossibile svuotare i dati del soggetto', 'error');
                                    } finally {
                                        setIsSvuotando(false);
                                    }
                                }}
                                className="px-5 py-2 rounded-lg font-bold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isSvuotando && <Loader2 className="animate-spin" size={16}/>} Svuota definitivamente
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modale dati identità per la firma */}
            {showDatiIdentitaModal && (
                <Modal title="Dati Identità per la Firma" onClose={() => setShowDatiIdentitaModal(false)}>
                    <div className="space-y-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex gap-2 items-start text-sm text-blue-700">
                            <AlertCircle size={16} className="shrink-0 mt-0.5 text-blue-500"/>
                            <span>I campi con <span className="inline-block w-2 h-2 rounded-full bg-green-500 align-middle mx-0.5"/> sono già salvati nella scheda cliente. Completa i campi mancanti prima di inviare.</span>
                        </div>

                        {/* Riconoscimento de visu — campo legale separato in evidenza.
                            Dichiara che l'operatore Investiga ha identificato il cliente
                            di persona prima della firma. Rafforza il valore probatorio
                            della FEA. OTP API v1.1 campo recognized_de_visu_on.
                            Toggle "Cliente da identificare a distanza": azzera la data,
                            così OTP fa partire il riconoscimento remoto (foto documento
                            + selfie) lato cliente invece di assumere identificazione in
                            presenza.
                            Nascosto per i mandati investigativi: per scelta operativa
                            il riconoscimento è sempre a distanza, quindi la sezione
                            confonderebbe (e il prefill ha già azzerato la data).
                        {pratica?.modelId !== 'mand-investigativo' && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                            <label className="text-xs font-bold text-emerald-800 uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
                                Riconoscimento
                            </label>
                            <label className="flex items-start gap-2 mb-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={!datiIdentitaForm.recognizedDeVisuOn}
                                    onChange={e => setDatiIdentitaForm(f => ({
                                        ...f,
                                        recognizedDeVisuOn: e.target.checked
                                            ? ''
                                            : localIsoDate(),
                                    }))}
                                    className="mt-0.5 w-4 h-4 accent-emerald-600 cursor-pointer"
                                />
                                <span className="text-[11px] text-emerald-800 leading-snug">
                                    <strong>Cliente da identificare a distanza</strong> — il cliente
                                    caricherà foto del documento e selfie durante la firma.
                                </span>
                            </label>
                            {datiIdentitaForm.recognizedDeVisuOn && (
                                <>
                                    <p className="text-[11px] text-emerald-700 mb-2 leading-snug">
                                        Data in cui hai identificato di persona il cliente. Rafforza il valore
                                        probatorio della firma.
                                    </p>
                                    <input
                                        type="date"
                                        value={datiIdentitaForm.recognizedDeVisuOn}
                                        onChange={e => setDatiIdentitaForm(f => ({ ...f, recognizedDeVisuOn: e.target.value }))}
                                        max={localIsoDate()}
                                        className="border border-emerald-300 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 w-full sm:w-auto"
                                    />
                                </>
                            )}
                        </div>
                        )}*/}

                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                    Data di nascita <span className="text-red-400">*</span>
                                    {datiIdentitaFromClient.has('dataNascita') && <span className="w-2 h-2 rounded-full bg-green-500"/>}
                                </label>
                                <input type="date"
                                    value={datiIdentitaForm.dataNascita}
                                    onChange={e => setDatiIdentitaForm(f => ({ ...f, dataNascita: e.target.value }))}
                                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                    Codice fiscale <span className="text-red-400">*</span>
                                    {datiIdentitaFromClient.has('codiceFiscale') && <span className="w-2 h-2 rounded-full bg-green-500"/>}
                                </label>
                                <input type="text" placeholder="Es. RSSMRA80A01H501U" maxLength={16}
                                    value={datiIdentitaForm.codiceFiscale}
                                    onChange={e => setDatiIdentitaForm(f => ({ ...f, codiceFiscale: e.target.value.toUpperCase() }))}
                                    onBlur={e => {
                                        // Normalizzazione al blur: corregge O↔0 / I↔1 quando il CF ha 16 caratteri.
                                        const normalized = normalizeFiscalCode(e.target.value);
                                        if (normalized !== e.target.value.toUpperCase()) {
                                            setDatiIdentitaForm(f => ({ ...f, codiceFiscale: normalized }));
                                        }
                                    }}
                                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 uppercase font-mono tracking-wider"
                                />
                            </div>

                            {/* Anagrafica FEA (sesso, cittadinanza, nascita, residenza) */}
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                    Sesso <span className="text-red-400">*</span>
                                </label>
                                <select value={datiIdentitaForm.gender}
                                    onChange={e => setDatiIdentitaForm(f => ({ ...f, gender: e.target.value }))}
                                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white">
                                    <option value="">Seleziona...</option>
                                    <option value="M">Maschio</option>
                                    <option value="F">Femmina</option>
                                </select>
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                    Cittadinanza <span className="text-red-400">*</span>
                                </label>
                                <input type="text" list="otp-nazioni-list" placeholder="Italia"
                                    value={COUNTRIES.find(c => c.code === datiIdentitaForm.cittadinanzaNazione)?.name
                                        || datiIdentitaForm.cittadinanzaNazione}
                                    onChange={e => {
                                        const code = findCountryCodeByName(e.target.value);
                                        setDatiIdentitaForm(f => ({ ...f, cittadinanzaNazione: code || e.target.value.toUpperCase() }));
                                    }}
                                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                    Nazione di nascita <span className="text-red-400">*</span>
                                </label>
                                <input type="text" list="otp-nazioni-list" placeholder="Italia"
                                    value={COUNTRIES.find(c => c.code === datiIdentitaForm.nascitaNazione)?.name
                                        || datiIdentitaForm.nascitaNazione}
                                    onChange={e => {
                                        const code = findCountryCodeByName(e.target.value);
                                        setDatiIdentitaForm(f => ({ ...f, nascitaNazione: code || e.target.value.toUpperCase() }));
                                    }}
                                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                />
                            </div>

                            {datiIdentitaForm.nascitaNazione === 'IT' && (
                                <>
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                            Provincia di nascita <span className="text-red-400">*</span>
                                        </label>
                                        <input type="text" list="otp-province-list" placeholder="Es. MI" maxLength={2}
                                            value={datiIdentitaForm.nascitaProvincia}
                                            onChange={e => {
                                                const nuova = e.target.value.toUpperCase();
                                                setDatiIdentitaForm(f => {
                                                    // Se il comune è un capoluogo di un'altra provincia lo azzeriamo; un codice
                                                    // inserito a mano per un comune minore resta.
                                                    const hit = COMUNI_ITALIANI.find(c => c.codice === f.nascitaComune);
                                                    const incoerente = hit && nuova && hit.sigla !== nuova;
                                                    return { ...f, nascitaProvincia: nuova, ...(incoerente ? { nascitaComune: '' } : {}) };
                                                });
                                            }}
                                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 uppercase"
                                        />
                                    </div>

                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                            Comune di nascita <span className="text-red-400">*</span>
                                        </label>
                                        <input type="text" list="otp-comuni-nascita-list"
                                            placeholder={datiIdentitaForm.nascitaProvincia
                                                ? `Comuni di ${datiIdentitaForm.nascitaProvincia} (o codice)`
                                                : 'Nome o codice (es. Milano o F205)'}
                                            value={(() => {
                                                const hit = COMUNI_ITALIANI.find(c => c.codice === datiIdentitaForm.nascitaComune);
                                                return hit ? hit.nome : datiIdentitaForm.nascitaComune;
                                            })()}
                                            onChange={e => {
                                                const code = findCodiceComuneByName(e.target.value);
                                                setDatiIdentitaForm(f => ({ ...f, nascitaComune: code || e.target.value.toUpperCase() }));
                                            }}
                                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                        />
                                        {/*
                                          Filtrato per provincia se selezionata. Un codice catastale scritto a mano
                                          (comuni minori non in elenco) viene salvato comunque.
                                        */}
                                        <datalist id="otp-comuni-nascita-list">
                                            {COMUNI_ITALIANI
                                                .filter(c => !datiIdentitaForm.nascitaProvincia || c.sigla === datiIdentitaForm.nascitaProvincia)
                                                .map(c => (
                                                    <option key={c.codice} value={c.nome}>{c.codice} · {c.sigla}</option>
                                                ))}
                                        </datalist>
                                    </div>
                                </>
                            )}

                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                    Nazione di residenza <span className="text-red-400">*</span>
                                </label>
                                <input type="text" list="otp-nazioni-list" placeholder="Italia"
                                    value={COUNTRIES.find(c => c.code === datiIdentitaForm.residenzaNazione)?.name
                                        || datiIdentitaForm.residenzaNazione}
                                    onChange={e => {
                                        const code = findCountryCodeByName(e.target.value);
                                        setDatiIdentitaForm(f => ({ ...f, residenzaNazione: code || e.target.value.toUpperCase() }));
                                    }}
                                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                />
                            </div>

                            {datiIdentitaForm.residenzaNazione === 'IT' && (
                                <>
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                            Provincia di residenza <span className="text-red-400">*</span>
                                            {datiIdentitaFromClient.has('provincia') && <span className="w-2 h-2 rounded-full bg-green-500"/>}
                                        </label>
                                        <input type="text" list="otp-province-list" placeholder="Es. MI" maxLength={2}
                                            value={datiIdentitaForm.provincia}
                                            onChange={e => {
                                                const nuova = e.target.value.toUpperCase();
                                                setDatiIdentitaForm(f => {
                                                    const hit = COMUNI_ITALIANI.find(c => c.codice === f.residenzaComune);
                                                    const incoerente = hit && nuova && hit.sigla !== nuova;
                                                    return { ...f, provincia: nuova, ...(incoerente ? { residenzaComune: '' } : {}) };
                                                });
                                            }}
                                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 uppercase"
                                        />
                                    </div>

                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                            Comune di residenza <span className="text-red-400">*</span>
                                        </label>
                                        <input type="text" list="otp-comuni-residenza-list"
                                            placeholder={datiIdentitaForm.provincia
                                                ? `Comuni di ${datiIdentitaForm.provincia} (o codice)`
                                                : 'Nome o codice (es. Milano o F205)'}
                                            value={(() => {
                                                const hit = COMUNI_ITALIANI.find(c => c.codice === datiIdentitaForm.residenzaComune);
                                                return hit ? hit.nome : datiIdentitaForm.residenzaComune;
                                            })()}
                                            onChange={e => {
                                                const code = findCodiceComuneByName(e.target.value);
                                                setDatiIdentitaForm(f => ({ ...f, residenzaComune: code || e.target.value.toUpperCase() }));
                                            }}
                                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                        />
                                        <datalist id="otp-comuni-residenza-list">
                                            {COMUNI_ITALIANI
                                                .filter(c => !datiIdentitaForm.provincia || c.sigla === datiIdentitaForm.provincia.toUpperCase())
                                                .map(c => (
                                                    <option key={c.codice} value={c.nome}>{c.codice} · {c.sigla}</option>
                                                ))}
                                        </datalist>
                                    </div>

                                    <div className="flex flex-col gap-1 col-span-2">
                                        <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                            Indirizzo residenza <span className="text-red-400">*</span>
                                            {datiIdentitaFromClient.has('indirizzo') && <span className="w-2 h-2 rounded-full bg-green-500"/>}
                                        </label>
                                        <input type="text" placeholder="Via Roma 1"
                                            value={datiIdentitaForm.indirizzo}
                                            onChange={e => setDatiIdentitaForm(f => ({ ...f, indirizzo: e.target.value }))}
                                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                        />
                                    </div>
                                </>
                            )}

                            {/* Separatore tra residenza e documento. */}
                            <h4 className="col-span-2 text-[10px] font-bold uppercase tracking-widest text-indigo-700 pt-3 mt-2 border-t border-indigo-100">
                                Documento d'identità
                            </h4>

                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                    Tipo documento <span className="text-red-400">*</span>
                                    {datiIdentitaFromClient.has('docTipo') && <span className="w-2 h-2 rounded-full bg-green-500"/>}
                                </label>
                                <select value={datiIdentitaForm.docTipo}
                                    onChange={e => setDatiIdentitaForm(f => ({ ...f, docTipo: e.target.value }))}
                                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
                                >
                                    <option value="">Seleziona...</option>
                                    <option value="CI">Carta d'Identità</option>
                                    <option value="Passaporto">Passaporto</option>
                                    <option value="Patente">Patente</option>
                                </select>
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                    Numero documento <span className="text-red-400">*</span>
                                    {datiIdentitaFromClient.has('docNumero') && <span className="w-2 h-2 rounded-full bg-green-500"/>}
                                </label>
                                <input type="text" placeholder="Es. AB1234567"
                                    value={datiIdentitaForm.docNumero}
                                    onChange={e => setDatiIdentitaForm(f => ({ ...f, docNumero: e.target.value }))}
                                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                    Data rilascio <span className="text-red-400">*</span>
                                    {datiIdentitaFromClient.has('docDataRilascio') && <span className="w-2 h-2 rounded-full bg-green-500"/>}
                                </label>
                                <input type="date"
                                    value={datiIdentitaForm.docDataRilascio}
                                    onChange={e => setDatiIdentitaForm(f => ({ ...f, docDataRilascio: e.target.value }))}
                                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                    Data scadenza <span className="text-red-400">*</span>
                                    {datiIdentitaFromClient.has('docDataScadenza') && <span className="w-2 h-2 rounded-full bg-green-500"/>}
                                </label>
                                <input type="date"
                                    value={datiIdentitaForm.docDataScadenza}
                                    onChange={e => setDatiIdentitaForm(f => ({ ...f, docDataScadenza: e.target.value }))}
                                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                />
                            </div>

                            <div className="flex flex-col gap-1 col-span-2">
                                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                    Nazione emissione documento <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="text"
                                    list="otp-nazioni-list"
                                    placeholder="Inizia a digitare (es. Italia)"
                                    value={COUNTRIES.find(c => c.code === datiIdentitaForm.docNazioneEmissione)?.name
                                        || datiIdentitaForm.docNazioneEmissione}
                                    onChange={e => {
                                        // Si può scrivere il nome (dal datalist) o il codice: proviamo a risolvere il
                                        // nome in codice, altrimenti resta il valore inserito.
                                        const v = e.target.value;
                                        const code = findCountryCodeByName(v);
                                        setDatiIdentitaForm(f => ({ ...f, docNazioneEmissione: code || v.toUpperCase() }));
                                    }}
                                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                />
                                <datalist id="otp-nazioni-list">
                                    {COUNTRIES.map(c => (
                                        <option key={c.code} value={c.name}>{c.code}</option>
                                    ))}
                                </datalist>
                            </div>

                            {/* Provincia e città di emissione, obbligatorie solo per documenti italiani. */}
                            {datiIdentitaForm.docNazioneEmissione === 'IT' && (
                                <>
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                            Provincia emissione <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            list="otp-province-list"
                                            placeholder="Es. MI (Milano)"
                                            maxLength={2}
                                            value={datiIdentitaForm.docProvinciaEmissione}
                                            onChange={e => {
                                                const nuova = e.target.value.toUpperCase();
                                                setDatiIdentitaForm(f => {
                                                    const hit = COMUNI_ITALIANI.find(c => c.codice === f.docCittaEmissione);
                                                    const incoerente = hit && nuova && hit.sigla !== nuova;
                                                    return { ...f, docProvinciaEmissione: nuova, ...(incoerente ? { docCittaEmissione: '' } : {}) };
                                                });
                                            }}
                                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 uppercase"
                                        />
                                        <datalist id="otp-province-list">
                                            {PROVINCE_ITALIANE.map(p => (
                                                <option key={p.sigla} value={p.sigla}>{p.nome}</option>
                                            ))}
                                        </datalist>
                                    </div>

                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                            Comune emissione <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            list="otp-comuni-doc-list"
                                            placeholder={datiIdentitaForm.docProvinciaEmissione
                                                ? `Comuni di ${datiIdentitaForm.docProvinciaEmissione} (o codice)`
                                                : 'Nome comune o codice (es. Milano o F205)'}
                                            value={(() => {
                                                const hit = COMUNI_ITALIANI.find(c => c.codice === datiIdentitaForm.docCittaEmissione);
                                                return hit ? hit.nome : datiIdentitaForm.docCittaEmissione;
                                            })()}
                                            onChange={e => {
                                                const v = e.target.value;
                                                // Se il nome corrisponde a un comune salviamo il codice catastale, altrimenti
                                                // il valore inserito (può essere già un codice tipo "F205").
                                                const code = findCodiceComuneByName(v);
                                                setDatiIdentitaForm(f => ({ ...f, docCittaEmissione: code || v.toUpperCase() }));
                                            }}
                                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                        />
                                        <datalist id="otp-comuni-doc-list">
                                            {COMUNI_ITALIANI
                                                .filter(c => !datiIdentitaForm.docProvinciaEmissione || c.sigla === datiIdentitaForm.docProvinciaEmissione)
                                                .map(c => (
                                                    <option key={c.codice} value={c.nome}>{c.codice} · {c.sigla}</option>
                                                ))}
                                        </datalist>
                                        <span className="text-[10px] text-gray-400">Per comuni minori: digita il codice catastale (4 caratteri).</span>
                                    </div>
                                </>
                            )}

                        </div>

                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                onClick={() => { setShowDatiIdentitaModal(false); setShowFirmaModalOTP(true); }}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium"
                            >
                                Indietro
                            </button>
                            {/*<button
                                onClick={() => handleConfirmDatiIdentita({ dryRun: true })}
                                disabled={isFirmaLoading}
                                title="DEBUG: chiama l'endpoint dry-run, non crea il dossier su OTP, mostra il JSON del recipient. Zero costi."
                                className="px-4 py-2.5 rounded-xl font-bold border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 flex items-center gap-2 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                🐞 Debug
                            </button>*/}
                            <button
                                onClick={() => handleConfirmDatiIdentita()}
                                disabled={isFirmaLoading}
                                className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-indigo-700 flex items-center gap-2 shadow-lg shadow-indigo-200 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {isFirmaLoading
                                    ? <><Loader2 className="animate-spin" size={18}/> Invio in corso...</>
                                    : <><Smartphone size={18}/> Conferma e Invia</>
                                }
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {showHistoryModal && (
                <Modal title="Cronologia Versioni Pratica" onClose={() => setShowHistoryModal(false)}>
                    <div className="p-4 space-y-6 max-h-[70vh] overflow-y-auto">
                        {isLoadingHistory ? (
                            <div className="flex justify-center p-8"><Loader2 className="animate-spin text-blue-600" size={32} /></div>
                        ) : historyData.length === 0 ? (
                            <p className="text-gray-500 text-center">Nessuna versione precedente trovata.</p>
                        ) : (
                            <div className="relative border-l-2 border-blue-200 ml-3 space-y-8">
                                {historyData.map((item, index) => {
                                    const snapshot = item.snapshot;
                                    const isLatest = index === 0;
                                    const isExpanded = expandedVersionId === item.versionId;

                                    return (
                                        <div key={item.versionId} className="relative pl-6">
                                            {/* Punto della timeline */}
                                            <div className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full border-2 border-white ${isLatest ? 'bg-blue-600' : 'bg-gray-400'}`}></div>

                                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 shadow-sm">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div>
                                                        <div className="text-xs font-bold text-blue-600 flex items-center gap-1">
                                                            <Clock size={14}/> {new Date(item.versionDate).toLocaleString('it-IT')}
                                                        </div>
                                                        <h4 className="font-bold text-gray-800 mt-1">Stato: {snapshot?.stato || 'Sconosciuto'}</h4>
                                                    </div>
                                                    <div className="text-xs text-gray-500 bg-white px-2 py-1 rounded border">
                                                        Modificato da: {item.modifiedBy}
                                                    </div>
                                                </div>

                                                {snapshot?.note && (
                                                    <p className="text-sm text-gray-600 mb-3 bg-white p-2 rounded border border-gray-100">
                                                        <span className="font-semibold text-gray-700">Note: </span>
                                                        {snapshot.note}
                                                    </p>
                                                )}

                                                {/* File precedenti */}
                                                {snapshot?.files && snapshot.files.length > 0 && (
                                                    <div className="mt-3">
                                                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Documenti generati/attivi:</p>
                                                        <div className="flex flex-col gap-2">
                                                            {snapshot.files.map((file: any) => (
                                                                <button
                                                                    type="button"
                                                                    key={file.id}
                                                                    onClick={() => handleDownloadHistoryFile(file)}
                                                                    className="text-left text-sm text-blue-600 hover:underline flex items-center gap-2 bg-white p-2 rounded-lg border border-blue-100 hover:bg-blue-50 transition-colors"
                                                                >
                                                                    <FileText size={16}/> {file.name}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Espandi dati */}
                                                <div className="mt-4 pt-3 border-t border-gray-200">
                                                    <button
                                                        onClick={() => setExpandedVersionId(isExpanded ? null : item.versionId)}
                                                        className="text-sm font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors"
                                                    >
                                                        <FolderOpen size={16}/>
                                                        {isExpanded ? 'Nascondi dati completi' : 'Visualizza dati del modulo in questa versione'}
                                                    </button>
                                                </div>

                                                {/* Griglia dati (solo se espansa) */}
                                                {isExpanded && snapshot?.datiAggiuntivi && (
                                                    <div className="mt-4 bg-white p-4 rounded-lg border border-gray-200 shadow-inner animate-in fade-in slide-in-from-top-2">
                                                        <h5 className="font-bold text-gray-700 mb-3 border-b pb-2 text-sm">Contenuto Modulo Pratica</h5>

                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                                                            {Object.entries(snapshot.datiAggiuntivi).map(([key, val]) => {
                                                                // Nasconde campi vuoti, null e array/oggetti
                                                                if (val === '' || val === null || val === undefined) return null;
                                                                if (typeof val === 'object') return null;

                                                                // Booleani formattati
                                                                let displayVal = String(val);
                                                                if (typeof val === 'boolean') displayVal = val ? 'Sì' : 'No';

                                                                return (
                                                                    <div key={key} className="flex flex-col bg-gray-50 p-2 rounded border border-gray-100">
                                                                        <span className="text-gray-400 text-[10px] uppercase font-bold tracking-wider">{formatDatiKey(key)}</span>
                                                                        <span className="text-gray-800 font-medium whitespace-pre-wrap">{displayVal}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>

                                                        {/* Delegati */}
                                                        {snapshot?.delegati && snapshot.delegati.length > 0 && (
                                                            <div className="mt-6">
                                                                <h5 className="font-bold text-gray-700 mb-2 border-b pb-1 text-sm">Delegati Assegnati</h5>
                                                                <ul className="list-disc pl-5 text-sm space-y-1">
                                                                    {snapshot.delegati.map((d: any, idx: number) => (
                                                                        <li key={idx} className="text-gray-700">
                                                                            <strong>{d.nome}</strong> {d.qualifica ? `(${d.qualifica})` : ''}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>

                                                )}
                                                <div className="mt-4 pt-3 border-t border-gray-200 flex justify-end">
                                                    <button
                                                        onClick={() => navigate(`/pratiche/${id}/storico/${item.versionId}`)}
                                                        className="flex items-center gap-2 text-xs bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-bold transition-all shadow-sm active:scale-95"
                                                    >
                                                        <LayoutDashboard size={14} />
                                                        Apri Sola Lettura a pagina intera &rarr;
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </Modal>
            )}

            <ConflittiDisponibilitaModal
                open={conflittiAssegnazione.length > 0}
                onClose={() => { setConflittiAssegnazione([]); setPendingAssignPayload(null); }}
                collaboratoreNome={conflittiSummary.collab}
                conflitti={conflittiAssegnazione}
                creatiCount={conflittiSummary.creati}
                totaleRichiesto={conflittiSummary.totale}
                onConferma={pendingAssignPayload ? handleConfermaOverride : undefined}
            />
        </div>
    );
};

export default DettaglioPraticaView;